import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Search, Lock, Zap, ShieldCheck, KeyRound, Shield, Copy, Download, Package, Rocket, FileText, Radar } from "lucide-react";
import { toast } from "sonner";
import { ResolvedSchema, SchemaCatalog, inlineSchema, describeType } from "./ApiReferenceSchema";
import { sampleValue, SampleCodeTabs, TryItPanel } from "./ApiReferenceSamples";
import { buildCatalogSections } from "./ApiReferenceCatalog";
import { getBackendUrl as resolveBackendUrl } from "@/lib/runtimeConfig";
import { StudyBuildRecipe } from "@/components/apireference/StudyBuildRecipe";
import ChangeHistory, { LatestChangeChip } from "@/components/apireference/ChangeHistory";

const METHOD_COLORS = {
    GET: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
    POST: "text-cyan-400 bg-cyan-400/10 border-cyan-400/30",
    PATCH: "text-amber-400 bg-amber-400/10 border-amber-400/30",
    PUT: "text-amber-400 bg-amber-400/10 border-amber-400/30",
    DELETE: "text-rose-400 bg-rose-400/10 border-rose-400/30",
};

/** Curated sections — only the endpoint families clinical-ops integrators
 *  actually need.  Order here drives the on-page section order.
 *  Each `match` entry is `{ method, path }`.  `path` may be a literal
 *  OpenAPI path (with `{param}` placeholders) OR a regex string when
 *  multiple endpoints share a structural shape (e.g. dynamic flows). */
const CURATED_SECTIONS = [
    {
        id: "flows",
        title: "Flows",
        description:
            "Run published flows, poll their status, and stream live progress. Each currently-published flow also auto-generates its own typed endpoint (zap icon).",
        match: [
            { method: "GET", path: "/api/v1/flows/{slug}" },
            { method: "POST", path: "/api/v1/flows/{slug}" },
            { method: "POST", path: "/api/v1/flows/{slug}/{environment}" },
            { method: "GET", path: "/api/v1/flows/{slug}/runs/{run_id}" },
            { method: "GET", path: "/api/v1/flows/{slug}/runs/{run_id}/events" },
        ],
        // also include every dynamic-flow generated endpoint
        includeDynamic: true,
    },
    {
        id: "provision-client",
        title: "Provision a new client",
        description:
            "Create a tenant: mints API key, webhook signing secret, X25519 keypair, AES-256 DEK in one atomic call. Idempotent on `client_id`.",
        match: [
            { method: "POST", path: "/api/admin/clients/provision" },
        ],
    },
    {
        id: "deactivate-client",
        title: "Deactivate / re-activate a client account",
        description:
            "Soft-disable a tenant (fail-closed at the API gateway) and bring it back online. The tenant's data is preserved.",
        match: [
            { method: "POST", path: "/api/admin/clients/{client_id}/disable" },
            { method: "POST", path: "/api/admin/clients/{client_id}/enable" },
        ],
    },
    {
        id: "update-client",
        title: "Update a client account",
        description:
            "Adjust per-tenant rate limits, monthly cost budgets, and the reuse-revenue charge override.",
        match: [
            { method: "PATCH", path: "/api/admin/clients/{client_id}/limits" },
        ],
    },
    {
        id: "protocol-ingest",
        title: "Protocol ingest",
        description:
            "Upload a clinical protocol document (primary + supplements). The platform extracts visit schedule, assessments, and inclusion/exclusion criteria via Claude Sonnet 5.5. Two transports: multipart (one POST with `files=` array) or streaming (raw binary body with metadata via query params — ergonomic for SDKs and `curl -T`). Optional `contexts` (the `protocol` context is always kept) and validity metadata fields are accepted on both transports.",
        match: [
            { method: "POST", path: "/api/admin/protocols/ingest" },
            { method: "POST", path: "/api/v1/ingest/protocol" },
            { method: "POST", path: "/api/v1/ingest/protocol/stream" },
            { method: "GET", path: "/api/v1/ingest/protocol/{protocol_id}" },
        ],
    },
    {
        id: "cta-ingest",
        title: "CTA ingest",
        description:
            "Ingest a Clinical Trial Agreement — a legal / financial document. Async — poll the GET status endpoint or subscribe to the webhook for the parsed result. Optional `contexts` (repeat or comma-separate; the `cta` context is always kept) and contract validity metadata (`valid_from`, `valid_to`, `expires_at`, `effective_date`, `version_label`, `status`, …) travel with the document into the Data Lake. IRB approval fields do not apply to CTAs — consent forms use the ICF ingest.",
        match: [
            { method: "POST", path: "/api/admin/study-assets/ctas/ingest" },
            { method: "POST", path: "/api/v1/ingest/cta" },
            { method: "GET", path: "/api/v1/ingest/cta/{cta_id}" },
        ],
    },
    {
        id: "budget-ingest",
        title: "Budget ingest",
        description:
            "Ingest a study budget — an Excel workbook (`.xlsx` / `.xlsm`) or a PDF payment schedule. Procedure pricing is matched against the global pricing memory to flag mis-priced rows. Poll GET for status. Optional `contexts` and budget validity metadata (`valid_from`, `valid_to`, `expires_at`, `effective_date`, `version_label`, `status`) are accepted; a budget is a financial document, so IRB fields are not accepted here.",
        match: [
            { method: "POST", path: "/api/admin/study-assets/budgets/ingest" },
            { method: "POST", path: "/api/v1/ingest/budget" },
            { method: "GET", path: "/api/v1/ingest/budget/{budget_id}" },
        ],
    },
    {
        id: "icf-ingest",
        title: "ICF ingest (informed consent)",
        description:
            "Ingest an informed consent form — **PDF only** — with its IRB / EC approval metadata (`irb_approved`, `irb_approval_date`, `irb_name`), consent `version_label`, validity window (`valid_from`, `valid_to`, `expires_at`), `language` and an optional `protocol_id` link. Separate from the budget / CTA ingests on purpose: those are financial documents and carry no IRB fields. The ICF lands in the Data Lake with `kind: icf`, the `icf` context locked, a computed `validity_state`, and joins the version chain of earlier consent versions automatically; `GET /api/v1/ingest/icf/{id}` returns the record.",
        match: [
            { method: "POST", path: "/api/v1/ingest/icf" },
            { method: "GET", path: "/api/v1/ingest/icf/{icf_id}" },
        ],
    },
    {
        id: "ingest-files",
        title: "Ingest Files · EDC Guidelines, Manual, Investigator Brochure, Newsletter, Training, Other",
        description:
            "One standard ingestion for every study document that is not a protocol, budget, CTA or ICF. `POST /api/v1/ingest/file` takes the document and a `file_type` (`edc_guidelines` · `manual` · `investigator_brochure` · `newsletter` · `training` · `other`), optional `study_id` / `protocol_number`, `version_label`, `title`, `notes`. The file is stored in object storage, its text extracted, Claude writes a summary with key topics (and reads the document's own date / version), a Data Lake entry is created under the type's context (`edc_manual`, `manual`, `investigator_brochure`, `newsletter`, `training`, `other`) and the file joins a **version chain** per (client, study, type) — a new upload never overwrites the previous version (`supersedes` / `superseded_by`, `is_current_version`). **EDC Guidelines** additionally run the EDC framework extraction (form families + fields → `framework_id`) that eSource form generation reads; only EDC Guidelines files can be picked for form generation. The job flows through the same queue / webhook / polling as the other ingests; on `done` the result carries `file_id`, `version_label`, `datalake_item_id`, `framework_id`, `summary`, `key_topics`. `GET /api/v1/ingest/file/{file_id}` returns the record. The console page is QA → Ingest Files (admin API under `/api/admin/ingest-files`: list as version chains, detail with excerpt and extracted families, download, delete). **Deprecated aliases** kept working: `POST /api/v1/ingest/edc-guidelines` and `POST /api/admin/edc-frameworks/ingest` (= `file_type=edc_guidelines`), `GET /api/v1/library/edc-frameworks`.",
        match: [
            { method: "POST", path: "/api/v1/ingest/file" },
            { method: "GET", path: "/api/v1/ingest/file/{file_id}" },
            { method: "POST", path: "/api/admin/ingest-files" },
            { method: "GET", path: "/api/admin/ingest-files" },
            { method: "GET", path: "/api/admin/ingest-files/_meta" },
            { method: "GET", path: "/api/admin/ingest-files/{file_id}" },
            { method: "GET", path: "/api/admin/ingest-files/{file_id}/download" },
            { method: "DELETE", path: "/api/admin/ingest-files/{file_id}" },
            { method: "POST", path: "/api/v1/ingest/edc-guidelines" },
            { method: "GET", path: "/api/v1/library/edc-frameworks" },
            { method: "GET", path: "/api/v1/library/edc-frameworks/{framework_id}" },
        ],
    },
    {
        id: "document-ingest",
        title: "Document ingest · contexts & validity",
        description:
            "Any other site document — IRB letters, EDC / lab / pharmacy manuals, training, SOPs (consent forms have their own PDF-only ICF ingest). One or more `contexts` from the admin-managed vocabulary are required (unknown values are rejected with the accepted list); validity metadata is optional (`valid_from`, `valid_to`, `expires_at` defaults to `valid_to`, `document_date`, `version_label`, `effective_date`, `language`, `status` draft · approved · superseded · withdrawn, `notes`, `custom` JSON). IRB fields (`irb_approved`, `irb_approval_date`, `irb_name`) are accepted only when a chosen context is IRB-relevant — ICF, protocol, regulatory, safety (admins flag contexts under Settings → Data Lake Contexts); they are rejected on budgets, CTAs, manuals and the like. Text is extracted once, duplicates are detected by content hash, and the job flows through the same queue / webhook / polling as the other ingests. Every Data Lake row carries `contexts[]`, `doc_meta` and a computed `validity_state` (valid · not_yet_valid · expiring_soon ≤ 30 days · expired); expired / not-yet-valid documents are excluded from default searches and agent retrieval (`include_expired=true` to include). The owning client edits contexts and metadata with PATCH; list / search filter by `context`, `validity`, `include_expired`. **Bulk edit**: `POST …/items/bulk-metadata` applies one contexts change (`add` · `remove` · `replace`) and / or one metadata patch to up to 500 documents, audited per row. **Version chains**: versions of the same document are grouped automatically (protocol / CTA / budget by protocol number; others by client + primary context + normalised title — `v2`, `rev 3`, dates and extensions ignored) and can be overridden with `supersedes_id`; `GET …/versions` returns the timeline with the metadata fields that changed between consecutive versions, and admins can request an AI summary of the text changes per version pair (cached). **Context Usage Map**: `GET /api/admin/datalake/contexts/usage` counts the documents carrying each context — and how many are still valid — in this deployment and, on the Radar hub, in every registered environment (each spoke reports its counts in the sync heartbeat); super admins also get the per-client breakdown. Retiring a context (`PATCH …/contexts/{id}` with `active: false`) is refused with `409 context_in_use` (listing the environments and counts) while any environment still has documents with it. **Move & retire** (super admin): `POST /api/admin/datalake/contexts/{id}/move` `{\"to\": \"…\"}` replaces the context with another one on every document — here at once and, from the hub, in every environment (each spoke re-tags its own documents with its next control poll and acknowledges in its heartbeat) — and retires it automatically once none are left; progress via `GET /api/admin/datalake/contexts/moves`, stop with `POST …/contexts/moves/{move_id}/cancel`.",
        match: [
            { method: "POST", path: "/api/v1/ingest/document" },
            { method: "GET", path: "/api/v1/datalake/items" },
            { method: "GET", path: "/api/v1/datalake/items/{item_id}" },
            { method: "PATCH", path: "/api/v1/datalake/items/{item_id}" },
            { method: "POST", path: "/api/v1/datalake/items/bulk-metadata" },
            { method: "GET", path: "/api/v1/datalake/items/{item_id}/versions" },
            { method: "POST", path: "/api/v1/datalake/search" },
            { method: "GET", path: "/api/admin/datalake/contexts" },
            { method: "GET", path: "/api/admin/datalake/contexts/usage" },
            { method: "POST", path: "/api/admin/datalake/contexts/{context_id}/move" },
            { method: "GET", path: "/api/admin/datalake/contexts/moves" },
            { method: "POST", path: "/api/admin/datalake/contexts/moves/{move_id}/cancel" },
            { method: "POST", path: "/api/admin/datalake/contexts" },
            { method: "PATCH", path: "/api/admin/datalake/contexts/{context_id}" },
            { method: "POST", path: "/api/admin/datalake/items/bulk-metadata" },
            { method: "GET", path: "/api/admin/datalake/items/{item_id}/versions" },
            { method: "GET", path: "/api/admin/datalake/items/{item_id}/versions/candidates" },
            { method: "POST", path: "/api/admin/datalake/items/{item_id}/versions/summary" },
            { method: "POST", path: "/api/cron/datalake-validity-refresh" },
        ],
    },
    {
        id: "sitecentric-agent",
        title: "SiteCentric agent sessions (admin) — legacy",
        description:
            "**Legacy** — kept working for existing agents; new flows and agents should use a Connection with the `sitecentric_client_key` auth type (see *Connections · SiteCentric client-key auth*). Per client: market (US · CA · EU) and environment (US: development / staging / production; CA, EU: production only) derive the SiteCentric backend from a fixed table — no free-text URL. The platform exchanges the client's AIService API key at `POST {backend}/api/v1/ai-agent/token` for a session token that is cached per client and backend, renewed 60 s before expiry, dropped on 401 and never displayed or logged. Keys minted before encrypted key storage need one rotation. The `sitecentric.request` agent tool calls that backend only for the client the agent is working for; exchanges and calls are audited without secrets, a rejected key marks the connection broken and alerts admins. **Endpoint catalog** (Settings → SiteCentric Agent): the hand-maintained allow-list of operations the agent may call — `operation` id, method, path template, summary and parameters; `sitecentric.operations` lists it to the agent, uncatalogued paths are refused, and POST / PUT / PATCH / DELETE entries are callable only once an admin ticks *writes allowed*. Entries can also be imported from a pasted OpenAPI document and tried for one client.",
        match: [
            { method: "GET", path: "/api/admin/sitecentric-agent/markets" },
            { method: "GET", path: "/api/admin/sitecentric-agent/overview" },
            { method: "GET", path: "/api/admin/clients/{client_id}/sitecentric-agent" },
            { method: "PUT", path: "/api/admin/clients/{client_id}/sitecentric-agent" },
            { method: "POST", path: "/api/admin/clients/{client_id}/sitecentric-agent/test" },
            { method: "GET", path: "/api/admin/clients/{client_id}/sitecentric-agent/audit" },
            { method: "GET", path: "/api/admin/sitecentric-agent/catalog" },
            { method: "POST", path: "/api/admin/sitecentric-agent/catalog" },
            { method: "PUT", path: "/api/admin/sitecentric-agent/catalog/{entry_id}" },
            { method: "DELETE", path: "/api/admin/sitecentric-agent/catalog/{entry_id}" },
            { method: "POST", path: "/api/admin/sitecentric-agent/catalog/import" },
            { method: "POST", path: "/api/admin/sitecentric-agent/catalog/{entry_id}/try" },
        ],
    },
    {
        id: "connections-sitecentric",
        title: "Connections · SiteCentric client-key auth",
        description:
            "A Connection whose `auth_type` is `sitecentric_client_key` carries no stored secret: when a flow or agent runs for a client, the platform decrypts that client's agent-ready API key, posts it to `{base_url}{auth_config.token_path}` (default `/api/v1/ai-agent/token`), reads the token at `auth_config.token_field` and sends it as `Authorization: Bearer …` on every endpoint of the connection. The token lives exactly as long as the task — one exchange per flow run / agent run / scheduled run, reused by every `api_call` tile and `connection.call` tool invocation — then `auth_config.logout_path` (default `/api/v1/auth/logout`) is called and the token is forgotten. `GET …/sitecentric` reports whether a client has a usable key (`ready`, `key.source`), `GET …/sessions` shows the live task sessions (count and age only), `DELETE …/sessions` logs them all out. `_meta/catalog` lists the fixed SiteCentric backends (market × environment) to pick a `base_url` from. Keys, tokens and bodies never appear in results, audits or logs.",
        match: [
            { method: "GET", path: "/api/admin/connections/_meta/catalog" },
            { method: "GET", path: "/api/admin/connections/{connection_id}/sitecentric" },
            { method: "GET", path: "/api/admin/connections/{connection_id}/sessions" },
            { method: "DELETE", path: "/api/admin/connections/{connection_id}/sessions" },
        ],
    },
    {
        id: "scheduled-tasks",
        title: "Scheduled tasks (admin)",
        description:
            "Run a flow or an AI agent for one client on a schedule — presets (`every_minutes`, `hourly`, `daily`, `weekly`, with an IANA timezone) or a 5-field `cron` expression. `POST /preview` returns the human text and the next run times before saving; `POST /{task_id}/run` queues a run immediately; `/runs` is the per-task history linking to the flow run or agent run it produced. Agent tasks may list `context_tools` (e.g. `connection.call` against a SiteCentric connection) that are executed first and handed to the agent as context; the connection's token is held for the whole run and logged out afterwards. Permissions: `schedule.view`, `schedule.edit`, `schedule.run`. The platform cron (`.emergent/crons.yml`) calls `POST /api/cron/scheduled-tasks` every 5 minutes as a safety net with the `WEBHOOK_CRON_SECRET` bearer; the leader-elected in-process loop ticks every 30 s, so due tasks fire once.",
        match: [
            { method: "GET", path: "/api/admin/scheduled-tasks/_meta" },
            { method: "POST", path: "/api/admin/scheduled-tasks/preview" },
            { method: "GET", path: "/api/admin/scheduled-tasks" },
            { method: "POST", path: "/api/admin/scheduled-tasks" },
            { method: "GET", path: "/api/admin/scheduled-tasks/{task_id}" },
            { method: "PATCH", path: "/api/admin/scheduled-tasks/{task_id}" },
            { method: "DELETE", path: "/api/admin/scheduled-tasks/{task_id}" },
            { method: "POST", path: "/api/admin/scheduled-tasks/{task_id}/run" },
            { method: "GET", path: "/api/admin/scheduled-tasks/{task_id}/runs" },
            { method: "POST", path: "/api/cron/scheduled-tasks" },
        ],
    },
    {
        id: "study-build",
        title: "Study build · protocol + budget (+ CTA) → priced schedule",
        description:
            "Build a complete, site-ready study from a protocol, a budget and an optional CTA — send NEW FILES (multipart `POST /upload`: `protocol_file`, `budget_file`, `cta_file`) or reference documents already in the Data Lake (JSON `POST`: `protocol_id`, `budget_id`, `cta_id`), or mix both. Authenticate with the tenant's Client Token (`Authorization: Bearer <api_key>`). The call returns 202 with a build `id`, a `poll_url` and per-stage `progress`; poll `GET /{build_id}` until `status` is `ready` (full `build` JSON) or `failed`, or pass `webhook_url` to receive a signed `study_build.done` / `study_build.failed` callback. The schedule follows the protocol's Schedule of Events in order and is cohort-aware (identical visits across arms are not duplicated; cohort-specific visits carry `cohorts`). Budget visit columns and line items are reconciled with the schedule by AI with a rule-based fallback (`matching: ai | rules`), and every protocol-vs-budget disagreement is listed in `discrepancies[]` (`high` · `medium` · `low` · `info`) — the build completes with discrepancies rather than failing. The admin console uses the same engine via `/api/admin/study-assets/study-builds/jobs`. Full request / response JSON: see the recipe above and each endpoint's example.",
        match: [
            { method: "POST", path: "/api/v1/study-builds/upload" },
            { method: "POST", path: "/api/v1/study-builds" },
            { method: "GET", path: "/api/v1/study-builds/{build_id}" },
            { method: "GET", path: "/api/v1/study-builds" },
            { method: "POST", path: "/api/v1/study-builds/{build_id}/cancel" },
            { method: "POST", path: "/api/v1/study-builds/{build_id}/retry" },
            { method: "POST", path: "/api/admin/study-assets/study-builds/jobs" },
            { method: "GET", path: "/api/admin/study-assets/study-builds" },
            { method: "GET", path: "/api/admin/study-assets/study-builds/{build_id}" },
            { method: "POST", path: "/api/admin/study-assets/study-builds/{build_id}/cancel" },
            { method: "POST", path: "/api/admin/study-assets/study-builds/{build_id}/retry" },
            { method: "POST", path: "/api/admin/study-assets/study-builds" },
        ],
    },
    {
        id: "ripeness-push",
        title: "Trial Ripeness Radar · push opportunities",
        description:
            "Push up to 50 preprocessed opportunities per call (same shape as a news_api_ai row). Required per item: compound_name and article_url — the absolute http(s) URL of the source article (articleUrl also accepted); if any item lacks a valid URL the whole batch is rejected with 422 and nothing is stored. Async: 202 + batch_id → poll, or receive the ripeness.batch.done webhook. Dedupe on your id, then on a content fingerprint. Tenant API key or admin JWT.",
        match: [
            { method: "POST", path: "/api/v1/ripeness/opportunities/batch" },
            { method: "GET", path: "/api/v1/ripeness/opportunities/batch/{batch_id}" },
            { method: "GET", path: "/api/v1/ripeness/opportunities/batches" },
        ],
    },
    {
        id: "ripeness-read",
        title: "Trial Ripeness Radar · read, filter & sort",
        description:
            "Read-only Radar surface for CTMS / BD integrations. Every list filter has an exclude_* twin (include/exclude lists, OR within a field, AND across fields) — on the text fields (therapeutic_area · indication · compound · sponsor) exclude values are case-insensitive keywords, so exclude_therapeutic_area=oncology removes Oncology and Oncology/Nasal — incl. study_role (core · clinpharm · label_expansion), timing buckets (window_open_now · opening_within_12m · under_regulatory_review · overdue · frontier_running_long) and cro_status (confirmed · likely · confirmed_or_likely · none) — plus scalar ranges, free-text q, multi-key sort and stable pagination. Every row carries program_track_count, study_role, best_phase_role, timing_flags and cro. POST /tracks/query takes the same grammar as JSON; POST /facets/query takes that same body and returns self-excluding facet counts — every facet lists ALL its distinct values (no top-N cap) and search= keeps only the values containing the string. GET /areas and GET /indications return every unique value with counts for dropdowns — search= substring, sort=value|count; /areas always lists the complete 33-area body-system / specialty taxonomy (Oncology … Mental Health, Sleep Medicine, Hepatology, Rare & Genetic Diseases, Clinical Pharmacology) plus any approved custom areas — the mapping is kept up to date automatically (AI fallback + daily refresh, see the Therapeutic areas section). Tenant API key or admin JWT.",
        match: [
            { method: "GET", path: "/api/v1/ripeness/tracks" },
            { method: "POST", path: "/api/v1/ripeness/tracks/query" },
            { method: "GET", path: "/api/v1/ripeness/tracks/{track_id}" },
            { method: "GET", path: "/api/v1/ripeness/tracks/{track_id}/evaluations" },
            { method: "GET", path: "/api/v1/ripeness/programs/{program_id}" },
            { method: "GET", path: "/api/v1/ripeness/facets" },
            { method: "POST", path: "/api/v1/ripeness/facets/query" },
            { method: "GET", path: "/api/v1/ripeness/areas" },
            { method: "GET", path: "/api/v1/ripeness/indications" },
            { method: "GET", path: "/api/v1/ripeness/records/{source_record_id}/evaluation" },
        ],
    },
    {
        id: "ripeness-exclusions",
        title: "Trial Ripeness Radar · persisted exclusions",
        description:
            "Hide a track or a whole program from every subsequent list / facet call. Tenant keys create tenant-scoped rows; admins create global rows. Soft-delete to restore; include_excluded=true to peek at hidden rows.",
        match: [
            { method: "GET", path: "/api/v1/ripeness/exclusions" },
            { method: "POST", path: "/api/v1/ripeness/exclusions" },
            { method: "DELETE", path: "/api/v1/ripeness/exclusions/{exclusion_id}" },
        ],
    },
    {
        id: "ripeness-views",
        title: "Trial Ripeness Radar · saved views",
        description:
            "Store a POST /tracks/query body once under a name, then apply it with GET /views/{view_id}/tracks (list, with sort / paging overrides) and GET /views/{view_id}/facets (self-excluding counts) so the list and its filter counts always come from the same definition. Tenant keys own tenant-scoped views and can read admin-created global views; admins see and edit everything. Soft-delete keeps the audit trail.",
        match: [
            { method: "GET", path: "/api/v1/ripeness/views" },
            { method: "POST", path: "/api/v1/ripeness/views" },
            { method: "GET", path: "/api/v1/ripeness/views/{view_id}" },
            { method: "PUT", path: "/api/v1/ripeness/views/{view_id}" },
            { method: "DELETE", path: "/api/v1/ripeness/views/{view_id}" },
            { method: "GET", path: "/api/v1/ripeness/views/{view_id}/tracks" },
            { method: "GET", path: "/api/v1/ripeness/views/{view_id}/facets" },
        ],
    },
    {
        id: "ripeness-reeval",
        title: "Trial Ripeness Radar · daily targeted re-evaluation (admin + cron)",
        description:
            "Instead of re-scoring the whole corpus, each run selects tracks with a reason — cadence due, readout / gate expected this quarter, next-phase window opening within 90 days, overdue / under regulatory review / running long (weekly), momentum, sites pending, fresh news records — caps them by program and re-evaluates those programs whole. Preview the plan, start a run, poll it, read the schedule. Automated daily at 03:00 UTC by the platform cron (POST /api/cron/ripeness-reevaluate, WEBHOOK_CRON_SECRET bearer) with an in-app leader-elected fallback. Admin JWT only — tenant keys receive 403.",
        match: [
            { method: "GET", path: "/api/v1/ripeness/reevaluations/plan" },
            { method: "POST", path: "/api/v1/ripeness/reevaluations" },
            { method: "GET", path: "/api/v1/ripeness/reevaluations" },
            { method: "GET", path: "/api/v1/ripeness/reevaluations/{job_id}" },
            { method: "GET", path: "/api/v1/ripeness/reevaluations/schedule" },
            { method: "POST", path: "/api/cron/ripeness-reevaluate" },
        ],
    },
    {
        id: "ripeness-areas",
        title: "Trial Ripeness Radar · therapeutic areas — kept up to date (admin + cron)",
        description:
            "The 33-area body-system / specialty taxonomy maps every indication at write time; whatever the rules cannot place lands in Other. To keep that residue at zero without manual work, an AI fallback classifier (Claude) classifies every unmapped indication once — answers ≥ 80 % confidence become applied overrides (manual beats AI beats rules) and their tracks are re-projected immediately, lower-confidence answers are listed for review, non-disease labels are marked residual, and a missing specialty is filed as a proposal for a NEW area that only exists once an admin approves it (→ custom area listed by GET /areas). Runs automatically: platform cron daily 04:00 UTC (POST /api/cron/ripeness-area-refresh), after every backfill / batch / re-evaluation job, at startup and every 6 h. GET /areas/health is what the QA Radar 'Therapeutic areas' panel shows; PUT /areas/overrides/{label} pins any indication to an area. Admin JWT only — tenant keys receive 403. On Radar Sync spokes nothing is classified locally: the scheduled refresh runs on the hub (cron answers managed_by_hub) and a spoke forwards Refresh now, overrides, proposal decisions and custom-area removals to the hub (response carries forwarded: true + processed_on); the decisions — and the proposals and refresh runs — arrive on every environment by sync.",
        match: [
            { method: "GET", path: "/api/v1/ripeness/areas/health" },
            { method: "POST", path: "/api/v1/ripeness/areas/refresh" },
            { method: "GET", path: "/api/v1/ripeness/areas/overrides" },
            { method: "PUT", path: "/api/v1/ripeness/areas/overrides/{label}" },
            { method: "DELETE", path: "/api/v1/ripeness/areas/overrides/{label}" },
            { method: "GET", path: "/api/v1/ripeness/areas/proposals" },
            { method: "POST", path: "/api/v1/ripeness/areas/proposals/{proposal_id}/approve" },
            { method: "POST", path: "/api/v1/ripeness/areas/proposals/{proposal_id}/reject" },
            { method: "DELETE", path: "/api/v1/ripeness/areas/custom/{name}" },
            { method: "POST", path: "/api/cron/ripeness-area-refresh" },
        ],
    },
    {
        id: "ripeness-value",
        title: "Trial Ripeness Radar · lead value 2.0 — budgets, locations, weights, CTA calibration",
        description:
            "Every Radar row carries value (raw, lead-value-2.0) and value_weighted (\"value to us\"). if_awarded = patients/site × budget/patient + fixed fees, where budget/patient = phase range × indication factor × location index; expected = if_awarded × P(trial happens) × win rate × enrollment achieved × window still open × market fit; value_weighted = expected × the caller's indication weight (0.25–3, ranking only — never the raw dollars or the ripeness score). value.factors explains every step: budget_per_patient {source: manual | manual (locked) | client indication | client area | client phase | global indication | global area | global phase, n, k, weight, manual, phase_range, data}, indication_factor {value, level: indication | area | default}, location_index {value, countries[], note}, fixed_fees_detail. value_phase=best|1|2|3|4 picks the phase, include_value=false omits it, the list envelope adds value_total (Σ expected + weighted midpoints, has_weights, location_index, warnings[] — assumptions_untouched / no_weights / insufficient_data / no_own_data, shown as a flag at the top of the Radar), sort=expected_value:desc / value_if_awarded:desc / weighted_value:desc orders by value, the drill-down returns value_by_phase. The numbers come from an assumption set: tenant API keys own their set (inherits the global one), admin JWTs edit the global default or a client's set with ?client_id=. Assumptions: phase ranges, area_factors + indication_factors (indication overrides), location {countries {CC: share}, index_overrides} (US, CA, UK, EU-27; US = 1.00), country_index (global, built-in defaults), weights {areas, indications}, locks (budget.<phase> · indication.<label> · area.<Area> · fixed_fees → manual wins), calibration {auto, min_samples = k}. Every PUT needs a reason and is audited per field; null resets a field to the inherited value. CTA budgets (Datalake) calibrate per indication → area → phase: the client's own CTAs first, then every client's (k-anonymous); a group blends in once it has ≥ k = 5 budgets with weight n ÷ (n + k) (5 CTAs → 50 %, 20 → 80 %), normalised to the US baseline through the budget's site country; tables refresh at startup, every 6 h and after each budget upload (POST /calibration/apply now). POST /assumptions/preview shows the effect of a pending change (3 sample tracks, portfolio total, top-N ranking) without saving; POST /assumptions/locks locks / releases rows (keys null = all); GET /countries lists the index; GET /health (admin) classifies clients as manual / partly / mostly data-driven; GET /clients (admin) lists tenants for ?client_id=.",
        match: [
            { method: "GET", path: "/api/v1/ripeness/value/assumptions" },
            { method: "PUT", path: "/api/v1/ripeness/value/assumptions" },
            { method: "POST", path: "/api/v1/ripeness/value/assumptions/preview" },
            { method: "POST", path: "/api/v1/ripeness/value/assumptions/locks" },
            { method: "GET", path: "/api/v1/ripeness/value/assumptions/audit" },
            { method: "GET", path: "/api/v1/ripeness/value/calibration" },
            { method: "POST", path: "/api/v1/ripeness/value/calibration/apply" },
            { method: "GET", path: "/api/v1/ripeness/value/countries" },
            { method: "GET", path: "/api/v1/ripeness/value/health" },
            { method: "GET", path: "/api/v1/ripeness/value/clients" },
        ],
    },
    {
        id: "ripeness-sources",
        title: "Trial Ripeness Radar · article sources (admin + cron)",
        description:
            "Every track, batch item, drill-down and evaluation-history row carries source_url (newest article) and sources[] (every article with a URL — url, article_id, published_at, summary, source_record_id — newest first). summary is the article's complete note — never truncated (v2.7.0: the 200-character excerpt is gone; the track detail's evaluation.latest_note.summary and evidence.articles[].summary are complete too). Every pushed opportunity must carry article_url (articleUrl also accepted; article_id optional) — missing or non-http → 422; every CSV-ingested row needs an article URL column too. Historical records were ingested without URLs — the bounded NewsAPI.ai backfill (nightly 05:00 UTC via POST /api/cron/ripeness-source-backfill, or POST /sources/backfill now; 200 records per run by default) looks them up by compound ± 7 days, stores matches on the record + its evidence and re-projects the tracks; misses are marked no_match and never retried, so records whose article is outside the provider's archive keep source_url: null. GET /sources/status is what the QA Radar 'Article sources' panel shows. Admin JWT only.",
        match: [
            { method: "GET", path: "/api/v1/ripeness/sources/status" },
            { method: "POST", path: "/api/v1/ripeness/sources/backfill" },
            { method: "POST", path: "/api/cron/ripeness-source-backfill" },
        ],
    },
    {
        id: "ripeness-external",
        title: "Trial Ripeness Radar · extra sources, sponsor pipeline pages, finder agent (admin)",
        description:
            "Extra evidence beyond the news feed: SEC filings, press wires, sponsor pipeline pages (discovered + read by Claude) and CTIS. GET /external/status shows the sources, cached documents and the one-time run; POST /external/refresh starts it (POST /external/refresh/stop stops; POST /programs/{id}/external-refresh checks one program). GET /sponsor-pipelines lists every sponsor's pipeline page (found / not found / no readable text / blocked / error / reading) with the rows read; PUT corrects the URL (null → automatic discovery again), POST …/refresh re-reads it, POST …/finder lets the finder agent research one sponsor, GET /sponsor-pipelines/finder shows the agent's status and POST …/finder/run starts a pass. The agent runs daily (05:00 UTC) on every not-found / unreadable / blocked / error page: Wikidata, Claude, homepage links, common paths and pipeline PDFs to find the page; browser headers, a reader proxy, a free web proxy (AnyProxy), the Internet Archive and an optional HTTP proxy to read it — a way that works is kept (fetch_via). Not found → looked at again after 30 days. Hub-only (v2.7.0): on a Radar Sync spoke all of this is carried out on the hub — the spoke forwards each action (response: forwarded: true, processed_on) and receives documents, sponsor pages and finder state by sync; status endpoints answer managed_here: false. Admin JWT only.",
        match: [
            { method: "GET", path: "/api/v1/ripeness/external/status" },
            { method: "POST", path: "/api/v1/ripeness/external/refresh" },
            { method: "POST", path: "/api/v1/ripeness/external/refresh/stop" },
            { method: "GET", path: "/api/v1/ripeness/external/runs/{job_id}" },
            { method: "POST", path: "/api/v1/ripeness/programs/{program_id}/external-refresh" },
            { method: "GET", path: "/api/v1/ripeness/sponsor-pipelines" },
            { method: "PUT", path: "/api/v1/ripeness/sponsor-pipelines/{sponsor_key}" },
            { method: "POST", path: "/api/v1/ripeness/sponsor-pipelines/{sponsor_key}/refresh" },
            { method: "POST", path: "/api/v1/ripeness/sponsor-pipelines/{sponsor_key}/finder" },
            { method: "GET", path: "/api/v1/ripeness/sponsor-pipelines/finder" },
            { method: "POST", path: "/api/v1/ripeness/sponsor-pipelines/finder/run" },
        ],
    },
    {
        id: "ripeness-jobs",
        title: "Trial Ripeness Radar · backfill jobs (admin)",
        description:
            "Operator surface: bulk-ingest the news_api_ai CSV (every row needs an article URL — article_url / articleUrl / url column — else 422 with the line numbers) and run / poll / cancel corpus-wide backfill jobs. Admin JWT only — tenant keys receive 403.",
        match: [
            { method: "POST", path: "/api/v1/ripeness/backfill-jobs" },
            { method: "GET", path: "/api/v1/ripeness/backfill-jobs" },
            { method: "GET", path: "/api/v1/ripeness/backfill-jobs/{job_id}" },
            { method: "GET", path: "/api/v1/ripeness/backfill-jobs/{job_id}/report" },
            { method: "POST", path: "/api/v1/ripeness/backfill-jobs/{job_id}/cancel" },
            { method: "POST", path: "/api/v1/ripeness/news-csv/ingest" },
        ],
    },
    {
        id: "radar-sync-admin",
        title: "Radar Sync · hub administration (admin JWT)",
        description:
            "One hub and any number of spokes (replicas or standalone deployments) keep one Trial Ripeness Radar dataset in sync — two-way, the hub wins on conflict. As soon as a spoke is linked it downloads the hub's dataset automatically, then every 5 minutes it pushes its local changes up and pulls the hub's changes down. Every channel (dev · stage · prod) follows live hub data. Sync now works from both sides: POST /environments/{env_id}/sync on the hub (picked up by the spoke within 30 s), POST /run on the spoke; reconcile: true adds a full reconciliation. Twice a day (06:00 + 18:00 UTC — platform cron POST /api/cron/radar-sync-reconcile on the hub, plus each spoke's own schedule RADAR_SYNC_RECONCILE_HOURS_UTC) every spoke compares every natural key with the hub and repairs drift; POST /reconcile asks all environments now. Super admins register environments (one sync key each, shown once, bound to a channel), rotate or revoke keys, import an environment that cannot reach the hub, approve link requests and reset-to-blank requests. GET /status on a spoke shows cursors, pushes, the last reconciliation and recent runs.",
        match: [
            { method: "GET", path: "/api/admin/sync/mode" },
            { method: "GET", path: "/api/admin/sync/overview" },
            { method: "GET", path: "/api/admin/sync/channels" },
            { method: "GET", path: "/api/admin/sync/environments" },
            { method: "POST", path: "/api/admin/sync/environments" },
            { method: "POST", path: "/api/admin/sync/environments/{env_id}/rotate-key" },
            { method: "POST", path: "/api/admin/sync/environments/{env_id}/revoke" },
            { method: "DELETE", path: "/api/admin/sync/environments/{env_id}" },
            { method: "POST", path: "/api/admin/sync/environments/{env_id}/import" },
            { method: "POST", path: "/api/admin/sync/environments/{env_id}/sync" },
            { method: "POST", path: "/api/admin/sync/reconcile" },
            { method: "GET", path: "/api/admin/sync/imports" },
            { method: "GET", path: "/api/admin/sync/imports/{import_id}" },
            { method: "GET", path: "/api/admin/sync/resets" },
            { method: "POST", path: "/api/admin/sync/resets/{request_id}/approve" },
            { method: "POST", path: "/api/admin/sync/resets/{request_id}/deny" },
            { method: "GET", path: "/api/admin/sync/link-requests" },
            { method: "POST", path: "/api/admin/sync/link-requests/{link_id}/approve" },
            { method: "POST", path: "/api/admin/sync/link-requests/{link_id}/deny" },
            { method: "GET", path: "/api/admin/sync/link" },
            { method: "POST", path: "/api/admin/sync/link" },
            { method: "POST", path: "/api/admin/sync/link/poll" },
            { method: "POST", path: "/api/admin/sync/link/cancel" },
            { method: "POST", path: "/api/admin/sync/link/unlink" },
            { method: "GET", path: "/api/admin/sync/status" },
            { method: "POST", path: "/api/admin/sync/run" },
            { method: "POST", path: "/api/cron/radar-sync-reconcile" },
        ],
    },
    {
        id: "radar-sync-protocol",
        title: "Radar Sync · hub ↔ environment protocol (sync key)",
        description:
            "Bearer-authenticated with the environment's sync key (rsk_…). Every cycle a spoke calls the hub: GET /manifest (live sequence, estimated counts), GET /changes per collection with since/upto cursors (the same loop performs the first bootstrap and every delta; hub copies replace differing local ones — hub wins), then POST /push per collection with what is still a local change (base_seq = its pull cursor; new documents are added, an existing one changes only if the hub has not changed it meanwhile and the spoke's copy is fresher — otherwise hub_won and the hub's copy comes back in conflicts), POST /heartbeat. Every 30 s it takes pending Sync now / Reconcile requests with POST /control. Reconciliation pages GET /keys (natural key + sequence) and repairs with POST /docs. Tenant batches accepted on a replica are forwarded with POST /forwarded-batches and their results fetched back immediately. Hub-only Radar actions started on a spoke (sponsor pipeline corrections / re-reads, the pipeline finder agent, the extra-source refresh, therapeutic-area refresh / overrides / proposal decisions) are forwarded with POST /actions {action, params, actor}: the hub runs them exactly as its own admin API would, audits them as actor @ environment and answers {ok, status, body} or {ok: false, status, detail}; the results reach every environment by the regular sync (deletions as tombstones in GET /changes). Reset-to-blank requests travel the same way (POST /reset-requests, GET decision, completed / cancelled). Every deployment also serves these routes under its own RADAR_SYNC_KEY so the hub can import from it. Link requests are the only unauthenticated entry: POST /link-requests files one (rate-limited), then the spoke polls GET /link-requests/{link_id} with its one-time link token and receives the sync key exactly once after approval.",
        match: [
            { method: "GET", path: "/api/sync/manifest" },
            { method: "GET", path: "/api/sync/changes" },
            { method: "POST", path: "/api/sync/heartbeat" },
            { method: "POST", path: "/api/sync/push" },
            { method: "POST", path: "/api/sync/control" },
            { method: "GET", path: "/api/sync/keys" },
            { method: "POST", path: "/api/sync/docs" },
            { method: "POST", path: "/api/sync/actions" },
            { method: "POST", path: "/api/sync/forwarded-batches" },
            { method: "GET", path: "/api/sync/forwarded-batches/{hub_batch_id}" },
            { method: "POST", path: "/api/sync/reset-requests" },
            { method: "GET", path: "/api/sync/reset-requests/{request_id}" },
            { method: "POST", path: "/api/sync/reset-requests/{request_id}/completed" },
            { method: "POST", path: "/api/sync/reset-requests/{request_id}/cancelled" },
            { method: "POST", path: "/api/sync/link-requests" },
            { method: "GET", path: "/api/sync/link-requests/{link_id}" },
            { method: "POST", path: "/api/sync/link-requests/{link_id}/cancelled" },
        ],
    },
    {
        id: "shared-llm",
        title: "Shared LLM providers · network-wide credentials (admin JWT + sync key)",
        description:
            "Every environment of the Radar Sync network advertises the LLM credentials it holds in its heartbeat — provider, source (own key / built-in key), models, health and a SHA-256 fingerprint, never the key. The hub keeps the registry (GET /overview → providers) and a routing policy per credential (PUT /providers/{key}: enabled, priority, allowed workloads and environments, daily budget, rate limit, concurrency, tenant-data rule deny / same_region / allow); the owner authorises sharing on its own deployment (PUT /share, default off) and both must agree before a credential serves the network. A deployment without a usable key sends the completion to the hub (POST /api/sync/llm/execute): the hub checks the shared result cache, waits for an identical in-flight request instead of repeating it, picks eligible credentials by priority within quota, runs it with its own key or relays it to the owning spoke (which executes with its key and returns only the completion), fails over to the next credential and logs usage (GET /usage, GET /events). Spokes fetch the catalog of what they may use (GET /api/sync/llm/catalog) every 30 s and consult / publish shared-corpus results (GET|PUT /api/sync/llm/cache/{request_hash}). Regions (PUT /environments/{env_id}/region) feed the same_region rule. POST /test runs one tiny completion through the network only.",
        match: [
            { method: "GET", path: "/api/admin/llm-network/overview" },
            { method: "PUT", path: "/api/admin/llm-network/share" },
            { method: "PUT", path: "/api/admin/llm-network/providers/{key}" },
            { method: "PUT", path: "/api/admin/llm-network/environments/{env_id}/region" },
            { method: "GET", path: "/api/admin/llm-network/usage" },
            { method: "GET", path: "/api/admin/llm-network/events" },
            { method: "POST", path: "/api/admin/llm-network/test" },
            { method: "GET", path: "/api/admin/llm-network/warmup" },
            { method: "PUT", path: "/api/admin/llm-network/warmup" },
            { method: "POST", path: "/api/admin/llm-network/warmup/run" },
            { method: "GET", path: "/api/sync/llm/catalog" },
            { method: "POST", path: "/api/sync/llm/execute" },
            { method: "GET", path: "/api/sync/llm/cache/{request_hash}" },
            { method: "PUT", path: "/api/sync/llm/cache/{request_hash}" },
        ],
    },
    {
        id: "init-reset",
        title: "Setup · first super admin and reset to blank (public)",
        description:
            "POST /api/init creates the very first super admin once (410 afterwards). When setup is closed, /init offers a reset to blank that needs three independent signals: a request with a super admin's address of that environment (neutral 200 for any address), the e-mailed single-use confirmation (30 min), and a hub super admin's approval (24 h). Only then does the environment drop its whole database and reopen /init; linked environments re-bootstrap the Radar dataset from the hub. The hub itself answers 403.",
        match: [
            { method: "GET", path: "/api/init/status" },
            { method: "POST", path: "/api/init" },
            { method: "GET", path: "/api/init/reset/status" },
            { method: "POST", path: "/api/init/reset/request" },
            { method: "POST", path: "/api/init/reset/confirm" },
            { method: "POST", path: "/api/init/reset/cancel" },
        ],
    },
];

function makeSectionMatcher() {
    const literalKeys = new Set();
    const sectionByKey = new Map();
    const dynamicSections = [];
    for (const sec of CURATED_SECTIONS) {
        for (const m of sec.match || []) {
            const k = `${m.method.toUpperCase()} ${m.path}`;
            literalKeys.add(k);
            sectionByKey.set(k, sec);
        }
        if (sec.includeDynamic) dynamicSections.push(sec);
    }
    return { literalKeys, sectionByKey, dynamicSections };
}

export default function ApiReferencePage() {
    const [spec, setSpec] = useState(null);
    const [summary, setSummary] = useState(null);
    const [coverage, setCoverage] = useState(null);
    const [filter, setFilter] = useState("");
    const [expanded, setExpanded] = useState({});

    const load = async () => {
        try {
            const [s1, s2, s3] = await Promise.all([
                api.get(`/admin/api-reference/openapi`),
                api.get(`/admin/api-reference/summary`),
                api.get(`/admin/api-reference/coverage`),
            ]);
            setSpec(s1.data); setSummary(s2.data); setCoverage(s3.data);
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    };
    useEffect(() => { load(); }, []);

    const operations = useMemo(() => {
        if (!spec) return [];
        const out = [];
        for (const [path, methods] of Object.entries(spec.paths || {})) {
            for (const [method, op] of Object.entries(methods || {})) {
                if (!["get", "post", "put", "patch", "delete"].includes(method.toLowerCase())) continue;
                out.push({
                    path, method: method.toUpperCase(), op,
                    tags: op.tags || ["untagged"],
                    summary: op.summary || "",
                    description: op.description || "",
                    isDynamic: (op.tags || []).includes("dynamic-flows"),
                });
            }
        }
        return out;
    }, [spec]);

    /** Bucket matched operations into curated sections. */
    const sectioned = useMemo(() => {
        const { literalKeys, sectionByKey, dynamicSections } = makeSectionMatcher();
        const buckets = new Map(CURATED_SECTIONS.map((s) => [s.id, { ...s, ops: [] }]));
        for (const op of operations) {
            const key = `${op.method} ${op.path}`;
            if (literalKeys.has(key)) {
                const sec = sectionByKey.get(key);
                buckets.get(sec.id).ops.push(op);
                continue;
            }
            if (op.isDynamic) {
                for (const sec of dynamicSections) buckets.get(sec.id).ops.push(op);
            }
        }
        // Sort each bucket: curated literal order first, then dynamic flows alphabetical
        for (const sec of buckets.values()) {
            const literalOrder = new Map(
                (sec.match || []).map((m, i) => [`${m.method.toUpperCase()} ${m.path}`, i]),
            );
            sec.ops.sort((a, b) => {
                const ai = literalOrder.has(`${a.method} ${a.path}`) ? literalOrder.get(`${a.method} ${a.path}`) : 1000;
                const bi = literalOrder.has(`${b.method} ${b.path}`) ? literalOrder.get(`${b.method} ${b.path}`) : 1000;
                if (ai !== bi) return ai - bi;
                return a.path.localeCompare(b.path);
            });
        }
        // Everything else in the live spec — grouped by tag so the page
        // always renders 100 % of the API (new routes appear automatically).
        return [...buckets.values(), ...buildCatalogSections(operations, literalKeys)];
    }, [operations]);

    const filteredSections = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (!q) return sectioned;
        return sectioned
            .map((sec) => ({
                ...sec,
                ops: sec.ops.filter(
                    (o) =>
                        o.path.toLowerCase().includes(q)
                        || o.summary.toLowerCase().includes(q)
                        || sec.title.toLowerCase().includes(q)
                        || (o.tags || []).some((t) => String(t).toLowerCase().includes(q)),
                ),
            }))
            .filter((sec) => sec.ops.length > 0);
    }, [sectioned, filter]);

    const totalEndpoints = useMemo(
        () => sectioned.reduce((acc, s) => acc + s.ops.length, 0),
        [sectioned],
    );
    const curatedEndpoints = useMemo(
        () => sectioned.filter((s) => !s.catalog).reduce((acc, s) => acc + s.ops.length, 0),
        [sectioned],
    );
    const catalogAreas = useMemo(() => sectioned.filter((s) => s.catalog).length, [sectioned]);
    const firstCatalogId = useMemo(() => filteredSections.find((s) => s.catalog)?.id, [filteredSections]);

    return (
        <div className="px-8 py-8" data-testid="api-reference-page">
            <div className="flex items-start justify-between mb-6 gap-4">
                <div>
                    <div className="label-xs text-[#7C3AED]">
                        Developer Reference
                        {spec?.info?.version && <span className="ml-2 font-mono normal-case text-white/50" data-testid="api-ref-version">· API v{spec.info.version}</span>}
                    </div>
                    <h1 className="font-display text-4xl tracking-tight mt-2">API Reference</h1>
                    <p className="text-sm text-white/50 mt-2 max-w-2xl">
                        Every operation in the live OpenAPI spec, with runnable curl / Python / JavaScript
                        samples and an in-browser runner for GET calls. Curated integration guides first,
                        then the complete catalog grouped by area.
                        {summary && (
                            <> · <span data-testid="api-ref-summary-count">{curatedEndpoints} curated · {totalEndpoints} endpoints in total</span></>
                        )}
                    </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                    <a
                        href={`${resolveBackendUrl()}/api/openapi.json`}
                        target="_blank" rel="noreferrer"
                        data-testid="api-ref-openapi-link"
                        title="Public OpenAPI spec — no auth required. Point Postman / Insomnia / your codegen here."
                        className="text-xs text-white/50 hover:text-white border border-white/10 rounded-full px-3 py-1.5 whitespace-nowrap inline-flex items-center gap-1"
                    >
                        <Lock className="w-3 h-3 text-emerald-400" /> Public OpenAPI ↗
                    </a>
                    {coverage && (
                        <div data-testid="api-ref-coverage" className="text-[10px] text-white/40 font-mono text-right leading-tight">
                            <span className={coverage.routes.coverage_pct >= 80 ? "text-emerald-300" : coverage.routes.coverage_pct >= 50 ? "text-amber-300" : "text-rose-300"}>
                                {coverage.routes.coverage_pct}%
                            </span> routes documented ·
                            <span className={coverage.schemas.coverage_pct >= 80 ? " text-emerald-300" : coverage.schemas.coverage_pct >= 50 ? " text-amber-300" : " text-rose-300"}>
                                {" "}{coverage.schemas.coverage_pct}%
                            </span> schema fields
                        </div>
                    )}
                </div>
            </div>

            <div className="flex gap-3 mb-5">
                <div className="flex-1 relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                        value={filter} onChange={(e) => setFilter(e.target.value)}
                        placeholder="Search by path or section…"
                        data-testid="api-ref-filter"
                        className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm"
                    />
                </div>
            </div>

            {/* Section nav */}
            {spec && filteredSections.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6 items-center" data-testid="api-ref-section-nav">
                    {filteredSections.map((sec, i) => (
                        <React.Fragment key={sec.id}>
                            {sec.catalog && sec.id === firstCatalogId && (
                                <span className="basis-full text-[10px] font-mono uppercase tracking-wider text-white/30 mt-2" data-testid="api-ref-catalog-divider">
                                    Complete catalog · {catalogAreas} areas
                                </span>
                            )}
                            <a
                                href={`#${sec.id}`}
                                data-testid={`api-ref-nav-${sec.id}`}
                                className={`text-[11px] font-mono uppercase tracking-wider border rounded-full px-3 py-1.5 transition-all ${sec.catalog
                                    ? "text-white/40 hover:text-white border-white/5 hover:border-white/25"
                                    : "text-white/50 hover:text-white border-white/10 hover:border-white/30"}`}
                            >
                                {sec.title} <span className="text-white/30 ml-1">{sec.ops.length}</span>
                            </a>
                        </React.Fragment>
                    ))}
                </div>
            )}

            {!spec ? <div className="p-8 text-white/50">Loading reference…</div>
                : filteredSections.length === 0 ? (
                    <div className="p-8 text-white/50" data-testid="api-ref-empty">
                        No endpoints match your filter.
                    </div>
                ) : (
                    <>
                        <AuthenticationSection spec={spec} />
                        <SdkDownloadSection />
                        <PartnerProvisioningRecipe />
                        <ProtocolIngestRecipe />
                        <StudyBuildRecipe />
                        <RipenessRadarRecipe />
                        <div className="space-y-8" data-testid="api-ref-list">
                        {filteredSections.map((sec) => (
                            <section key={sec.id} id={sec.id} data-testid={`section-${sec.id}`}>
                                {sec.catalog && sec.id === firstCatalogId && (
                                    <div className="mb-6 mt-4 rounded-xl border border-white/10 bg-white/[0.03] px-5 py-4" data-testid="api-ref-catalog-heading">
                                        <div className="label-xs text-white/40">Complete catalog</div>
                                        <h2 className="font-display text-2xl tracking-tight mt-1">Every endpoint in the live spec</h2>
                                        <p className="text-sm text-white/50 mt-1 max-w-3xl">
                                            {totalEndpoints - curatedEndpoints} further operations across {catalogAreas} areas, grouped by OpenAPI tag.
                                            Each one carries its auth badge, parameters, schemas, curl / Python / JavaScript samples and — for GET
                                            calls — a live runner. New routes appear here automatically when the spec changes.
                                        </p>
                                    </div>
                                )}
                                <div className="mb-3">
                                    <h3 className="font-display text-xl tracking-tight">{sec.title}</h3>
                                    {sec.description && (
                                        <p className="text-sm text-white/50 mt-1 max-w-3xl">{sec.description}</p>
                                    )}
                                </div>
                                {sec.ops.length === 0 ? (
                                    <div className="rounded-xl border border-dashed border-white/10 px-4 py-6 text-xs text-white/40">
                                        No matching endpoints are currently registered. Publish a flow
                                        or check back after the next deploy.
                                    </div>
                                ) : (
                                    <div className="rounded-xl border border-white/10 overflow-hidden divide-y divide-white/5">
                                        {sec.ops.map((o, idx) => {
                                            const key = `${o.method}:${o.path}`;
                                            const isOpen = expanded[key];
                                            const authScheme = (o.op.security?.[0] && Object.keys(o.op.security[0])[0]) || null;
                                            const autoDoc = (o.description || "").includes("auto-synthesized");
                                            return (
                                                <div key={key} data-testid={`endpoint-${sec.id}-${idx}`} className="bg-white/[0.02] hover:bg-white/[0.04]">
                                                    <button
                                                        onClick={() => setExpanded((e) => ({ ...e, [key]: !e[key] }))}
                                                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left"
                                                    >
                                                        <span className={`text-[10px] tracking-wider px-2 py-0.5 rounded border ${METHOD_COLORS[o.method] || ""}`}>
                                                            {o.method}
                                                        </span>
                                                        <code className="text-xs text-white/80 truncate flex-1">{o.path}</code>
                                                        {o.isDynamic && <Zap className="w-3.5 h-3.5 text-violet-400" title="Dynamic flow" />}
                                                        {autoDoc && (
                                                            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-amber-400/30 text-amber-300/80 bg-amber-400/5"
                                                                title="Description auto-generated from the route signature — add a handler docstring for a richer description"
                                                                data-testid={`auto-doc-${o.method}-${o.path}`}>auto-doc</span>
                                                        )}
                                                        {authScheme && <AuthBadge scheme={authScheme} />}
                                                        <LatestChangeChip history={o.op["x-change-history"]} testId={`endpoint-version-${sec.id}-${idx}`} />
                                                        <span className="text-xs text-white/50 max-w-md truncate">{o.summary}</span>
                                                    </button>
                                                    {isOpen && <OperationDetail op={o} spec={spec} />}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </section>
                        ))}
                        </div>
                        <SchemaCatalog spec={spec} />
                    </>
                )}
        </div>
    );
}

// (Component definitions below.)

// =============================================================
// SDK download tile. One-click .tgz + copy-paste install snippet
// for the first-party Spring Boot starter. Manifest is fetched from
// /api/admin/sdk/java/manifest so we get the size/sha256 dynamically
// — bump the version in pom.xml and this tile updates on next load.
// =============================================================
function SdkDownloadSection() {
    const [manifest, setManifest] = useState(null);
    const [tab, setTab] = useState("maven");
    const [err, setErr] = useState(null);

    useEffect(() => {
        api.get("/admin/sdk/java/manifest")
            .then((r) => setManifest(r.data))
            .catch((e) => setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Unable to load SDK manifest"));
    }, []);

    const copy = (text, label) => {
        try { navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
        catch { /* clipboard blocked */ }
    };

    if (err) return (
        <section className="mb-10" data-testid="sdk-download-section-error">
            <div className="rounded-xl border border-rose-400/30 bg-rose-400/5 px-4 py-3 text-xs text-rose-200">
                SDK manifest unavailable: {err}
            </div>
        </section>
    );
    if (!manifest) return null;

    const downloadHref = `${resolveBackendUrl()}/api/sdk/java/download`;
    const kb = (manifest.size_bytes / 1024).toFixed(1);
    const snippetTabs = [
        { id: "maven",         label: "Maven (pom.xml)",     snippet: manifest.install_snippets.maven },
        { id: "gradle_kotlin", label: "Gradle (Kotlin DSL)", snippet: manifest.install_snippets.gradle_kotlin },
        { id: "gradle_groovy", label: "Gradle (Groovy DSL)", snippet: manifest.install_snippets.gradle_groovy },
        { id: "install",       label: "Install (CLI)",       snippet: manifest.install_snippets.unpack_and_install },
    ];
    const active = snippetTabs.find((t) => t.id === tab) || snippetTabs[0];

    return (
        <section id="sdk-downloads" data-testid="sdk-download-section" className="mb-10">
            <div className="mb-3 flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h3 className="font-display text-xl tracking-tight flex items-center gap-2">
                        <Package className="w-5 h-5 text-violet-300" /> First-party SDKs
                    </h3>
                    <p className="text-sm text-white/50 mt-1 max-w-3xl">
                        Drop-in clients that wrap the public API. Same release cadence as the server.
                    </p>
                </div>
            </div>
            <div className="rounded-xl border border-violet-400/30 bg-violet-400/[0.04] p-5">
                <div className="flex items-start gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="font-display text-lg">Java · Spring Boot 3 starter</span>
                            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-violet-400/40 text-violet-200">v{manifest.version}</span>
                            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-white/15 text-white/60">JDK {manifest.min_java_version}+</span>
                        </div>
                        <div className="text-[11px] font-mono text-white/45">
                            {manifest.group_id}:{manifest.artifact_id}:{manifest.version} · {kb} KB · sha256 <code className="text-white/65">{manifest.sha256.slice(0, 12)}…</code>
                        </div>
                    </div>
                    <a
                        href={downloadHref}
                        data-testid="sdk-download-java-btn"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-violet-400/15 border border-violet-400/40 text-violet-100 text-sm hover:bg-violet-400/25"
                    >
                        <Download className="w-4 h-4" /> Download .tgz
                    </a>
                </div>
                <div className="mt-4">
                    <div className="flex items-center gap-1 mb-2 flex-wrap" data-testid="sdk-tabs">
                        {snippetTabs.map((t) => (
                            <button
                                key={t.id}
                                onClick={() => setTab(t.id)}
                                data-testid={`sdk-tab-${t.id}`}
                                className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border ${tab === t.id
                                    ? "border-violet-400/50 bg-violet-400/15 text-violet-100"
                                    : "border-white/10 text-white/55 hover:border-white/20"}`}
                            >{t.label}</button>
                        ))}
                    </div>
                    <div className="relative">
                        <pre className="text-[11px] bg-black/40 border border-white/10 rounded px-3 py-2 overflow-auto font-mono leading-relaxed text-violet-100 whitespace-pre" data-testid={`sdk-snippet-${active.id}`}>
                            {active.snippet}
                        </pre>
                        <button
                            onClick={() => copy(active.snippet, active.label)}
                            data-testid={`sdk-copy-${active.id}`}
                            className="absolute top-2 right-2 text-[10px] text-white/40 hover:text-white inline-flex items-center gap-1 bg-black/50 px-1.5 py-0.5 rounded"
                        ><Copy className="w-3 h-3" /> copy</button>
                    </div>
                </div>
            </div>
        </section>
    );
}

// =============================================================
// Partner Provisioning recipe — one-shot snippet showing the
// shortest path from a superdomain key → a fully-onboarded tenant
// → the tenant's first SDK call. Copy-paste-ready in curl /
// Python / Java so a Sales / Partner team can hand this to a new
// customer's engineering team and watch them be live in minutes.
// =============================================================
const PROVISIONING_RECIPE_BASE = resolveBackendUrl() || "https://your-aiservice-host.example";

const RECIPE_CURL = `# 1. Set your SiteCentric superdomain key. This is the platform-owner
#    \`tf_live_*\` key issued by SiteCentric to your provisioning automation.
SD_KEY="tf_live_REPLACE_ME"
API="${PROVISIONING_RECIPE_BASE}"

# 2. Provision a new tenant — atomic call returning api_key,
#    webhook_signing_secret, X25519 keypair, AES-256 DEK.
PROV=$(curl -sS -X POST "$API/api/admin/clients/provision" \\
  -H "Authorization: Bearer $SD_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "client_name": "Acme Pharma",
    "client_id":   "acme",
    "contact_email": "ops@acme.com"
  }')
echo "$PROV" | jq

# 3. Capture the new tenant's API key — it is shown ONCE. Store it
#    in your secret manager before this shell exits.
NEW_KEY=$(echo "$PROV" | jq -r '.api_key.plaintext_key')
echo "Store this NOW: $NEW_KEY"

# 4. First call as the new tenant — run the Protocol Intelligence flow.
curl -sS -X POST "$API/api/v1/flows/protocol-intelligence" \\
  -H "Authorization: Bearer $NEW_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "input":    { "protocol_text": "An example protocol synopsis ..." },
    "metadata": { "clientId": "acme" }
  }' | jq`;

const RECIPE_PYTHON = `"""One-shot: provision a tenant + make its first SDK call.
Requires: pip install httpx
Env:      AISERVICE_SUPERDOMAIN_KEY = tf_live_...
"""
import os
import httpx

API = "${PROVISIONING_RECIPE_BASE}"
SUPERDOMAIN_KEY = os.environ["AISERVICE_SUPERDOMAIN_KEY"]

with httpx.Client(base_url=API, timeout=30.0) as c:
    # 1. Provision a new tenant.
    r = c.post(
        "/api/admin/clients/provision",
        headers={"Authorization": f"Bearer {SUPERDOMAIN_KEY}"},
        json={
            "client_name":   "Acme Pharma",
            "client_id":     "acme",
            "contact_email": "ops@acme.com",
        },
    )
    r.raise_for_status()
    bundle = r.json()
    new_key = bundle["api_key"]["plaintext_key"]
    print(f"Provisioned {bundle['client_id']!r}; capture NOW: {new_key}")

    # 2. First call as the new tenant.
    run = c.post(
        "/api/v1/flows/protocol-intelligence",
        headers={"Authorization": f"Bearer {new_key}"},
        json={
            "input":    {"protocol_text": "An example protocol synopsis ..."},
            "metadata": {"clientId": bundle["client_id"]},
        },
    )
    run.raise_for_status()
    print("Flow run:", run.json())`;

const RECIPE_JAVA = `// One-shot: provision a tenant + make its first SDK call.
// Spring 6 RestClient — works in any Spring Boot 3.x app.
// (The typed first-party SDK at /api/sdk/java/download wraps
//  tenant-side calls; provisioning uses raw HTTP below.)
import org.springframework.web.client.RestClient;
import java.util.Map;

String SD_KEY = System.getenv("AISERVICE_SUPERDOMAIN_KEY"); // tf_live_...
String API    = "${PROVISIONING_RECIPE_BASE}";

RestClient http = RestClient.builder().baseUrl(API).build();

// 1. Provision a new tenant.
@SuppressWarnings("unchecked")
Map<String,Object> bundle = http.post()
    .uri("/api/admin/clients/provision")
    .header("Authorization", "Bearer " + SD_KEY)
    .header("Content-Type",  "application/json")
    .body(Map.of(
        "client_name",   "Acme Pharma",
        "client_id",     "acme",
        "contact_email", "ops@acme.com"
    ))
    .retrieve()
    .body(Map.class);

@SuppressWarnings("unchecked")
String newKey = (String) ((Map<String,Object>) bundle.get("api_key"))
    .get("plaintext_key");
System.out.println("Capture NOW: " + newKey);

// 2. First call as the new tenant.
@SuppressWarnings("unchecked")
Map<String,Object> run = http.post()
    .uri("/api/v1/flows/protocol-intelligence")
    .header("Authorization", "Bearer " + newKey)
    .header("Content-Type",  "application/json")
    .body(Map.of(
        "input",    Map.of("protocol_text", "An example protocol synopsis ..."),
        "metadata", Map.of("clientId", "acme")
    ))
    .retrieve()
    .body(Map.class);
System.out.println("Flow run: " + run);

// Production tip: drop \`aiservice.api-key=<newKey>\` into the new
// tenant's application.yml and inject \`AiServiceClient\` to call
// any published flow with one line:
//   ai.flows().run("protocol-intelligence", input);`;

const RECIPE_TABS = [
    { id: "curl",   label: "curl",   snippet: RECIPE_CURL },
    { id: "python", label: "Python", snippet: RECIPE_PYTHON },
    { id: "java",   label: "Java",   snippet: RECIPE_JAVA },
];

const INGEST_RECIPE_BASE = resolveBackendUrl() || "https://your-aiservice-host.example";

const INGEST_RECIPE_JAVA = `// Stream a binary protocol document (PDF/DOCX) into AIService.
// One typed call — no multipart envelope, no UTF-8 corruption, just the
// right contract.
import us.sitecentric.aiservice.client.AiServiceClient;
import us.sitecentric.aiservice.model.JobEnvelope;
import us.sitecentric.aiservice.model.JobResult;
import java.io.InputStream;
import java.time.Duration;

@Service
class ProtocolIngestService {
    private final AiServiceClient ai;        // auto-wired by the starter
    private final FileService fileService;

    ProtocolIngestService(AiServiceClient ai, FileService fileService) {
        this.ai = ai; this.fileService = fileService;
    }

    public String ingest(Protocol p) {
        try (InputStream stream = fileService.downloadFile(p.getFileDocument().getFilePath())) {
            JobEnvelope job = ai.ingest()
                    .protocolStream(stream)
                    .filename(p.getFileDocument().getOriginalName())
                    .siteId(p.getSiteId())                 // REQUIRED
                    .sponsor(p.getSponsor())
                    .protocolNumber(p.getProtocolNumber())
                    .version(p.getVersion())
                    .contentType("application/pdf")       // optional
                    .submit();

            // Block until the worker finishes (5-minute deadline). A failing job
            // retries itself (1 min, then every 5 min, 5x) — meanwhile its status
            // is "queued" with next_retry_at set, so allow a longer deadline.
            JobResult done = ai.ingest().awaitJob(job.jobId(), Duration.ofMinutes(5));
            // Still failed and "retryable"? POST /api/v1/ingest-jobs/{jobId}/retry
            // resumes it — protocols restart from the stored text, no re-upload.
            if (done.isFailed()) throw new IllegalStateException(done.error());
            return (String) done.result().get("id");      // protocol_id
        } catch (IOException ioe) {
            throw new UncheckedIOException(ioe);
        }
    }
}
`;

const INGEST_RECIPE_CURL = `# Stream a PDF as the request body. Send 'site_id' + 'filename'
# as QUERY PARAMETERS — they're required for non-test protocols.
API="${INGEST_RECIPE_BASE}"
KEY="tf_live_..."           # tenant API key

curl -X POST "$API/api/v1/ingest/protocol/stream?filename=acme-protocol-v3.pdf&site_id=SC-001&sponsor=Acme%20Pharma&protocol_number=ACME-2026-001&version=v3.2" \\
  -H "Authorization: Bearer $KEY" \\
  -H "Content-Type: application/pdf" \\
  --data-binary @acme-protocol-v3.pdf

# → { "job_id": "...", "status": "queued",
#     "poll_url": "/api/v1/ingest-jobs/<job_id>", ... }

# Poll until status is done or failed. A failing job retries itself
# (1 min, then every 5 min, 5 times) — meanwhile status is "queued"
# with next_retry_at + last_error set.
curl "$API/api/v1/ingest-jobs/<job_id>" -H "Authorization: Bearer $KEY"

# Still failed after that and "retryable": true? Retry it — protocol
# jobs resume from the text already stored (no re-upload):
curl -X POST "$API/api/v1/ingest-jobs/<job_id>/retry" -H "Authorization: Bearer $KEY"
`;

const INGEST_RECIPE_PYTHON = `"""Stream a binary protocol into AIService — Python httpx."""
import httpx, time

API = "${INGEST_RECIPE_BASE}"
KEY = "tf_live_..."

with open("acme-protocol-v3.pdf", "rb") as fh:
    r = httpx.post(
        f"{API}/api/v1/ingest/protocol/stream",
        params={
            "filename": "acme-protocol-v3.pdf",
            "site_id": "SC-001",           # REQUIRED
            "sponsor": "Acme Pharma",
            "protocol_number": "ACME-2026-001",
            "version": "v3.2",
        },
        headers={
            "Authorization": f"Bearer {KEY}",
            "Content-Type": "application/pdf",
        },
        content=fh,    # httpx streams when given a file-like object
        timeout=120,
    )
    r.raise_for_status()
    job = r.json()

# Poll until done.
retried = False
while True:
    s = httpx.get(
        f"{API}/api/v1/ingest-jobs/{job['job_id']}",
        headers={"Authorization": f"Bearer {KEY}"},
        timeout=30,
    ).json()
    if s["status"] == "failed" and s.get("retryable") and not retried:
        # Auto-retries used up — resume once more (new set of auto-retries).
        retried = True
        httpx.post(f"{API}/api/v1/ingest-jobs/{job['job_id']}/retry",
                   headers={"Authorization": f"Bearer {KEY}"}, timeout=30).raise_for_status()
        s["status"] = "queued"
    if s["status"] in ("done", "failed"):
        print(s); break
    time.sleep(2)
`;

const INGEST_RECIPE_TABS = [
    { id: "java",   label: "Java",   snippet: INGEST_RECIPE_JAVA },
    { id: "curl",   label: "curl",   snippet: INGEST_RECIPE_CURL },
    { id: "python", label: "Python", snippet: INGEST_RECIPE_PYTHON },
];

function ProtocolIngestRecipe() {
    const [tab, setTab] = useState("java");
    const active = INGEST_RECIPE_TABS.find((t) => t.id === tab) || INGEST_RECIPE_TABS[0];
    const copy = (text, label) => {
        try { navigator.clipboard.writeText(text); toast.success(`${label} snippet copied`); }
        catch { /* clipboard blocked */ }
    };
    return (
        <section id="protocol-ingest-recipe" data-testid="protocol-ingest-recipe-section" className="mb-10">
            <div className="mb-3 flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h3 className="font-display text-xl tracking-tight flex items-center gap-2">
                        <FileText className="w-5 h-5 text-cyan-300" /> Protocol ingest · stream-from-InputStream
                    </h3>
                    <p className="text-sm text-white/50 mt-1 max-w-3xl">
                        Send the raw bytes of a PDF / DOCX protocol over{" "}
                        <code className="px-1 bg-white/5 rounded">application/octet-stream</code>.
                        <strong className="text-rose-200"> Don&apos;t</strong> decode binary files as
                        UTF-8 strings and stuff them into JSON — that irreversibly corrupts the
                        document and the AI extractor sees garbage.
                    </p>
                </div>
            </div>
            <div className="rounded-xl border border-cyan-400/30 bg-cyan-400/[0.04] p-5">
                <div className="text-[11px] uppercase tracking-wider text-cyan-200/80 mb-3">
                    Stream → poll (or webhook)
                </div>
                <div className="flex items-center gap-1 mb-2 flex-wrap" data-testid="protocol-ingest-recipe-tabs">
                    {INGEST_RECIPE_TABS.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`protocol-ingest-recipe-tab-${t.id}`}
                            className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border ${tab === t.id
                                ? "border-cyan-400/50 bg-cyan-400/15 text-cyan-100"
                                : "border-white/10 text-white/55 hover:border-white/20"}`}
                        >{t.label}</button>
                    ))}
                </div>
                <div className="relative">
                    <pre
                        className="text-[11px] bg-black/40 border border-white/10 rounded px-3 py-3 overflow-auto font-mono leading-relaxed text-cyan-100 whitespace-pre max-h-[480px]"
                        data-testid={`protocol-ingest-recipe-snippet-${active.id}`}
                    >
                        {active.snippet}
                    </pre>
                    <button
                        onClick={() => copy(active.snippet, active.label)}
                        data-testid={`protocol-ingest-recipe-copy-${active.id}`}
                        className="absolute top-2 right-2 text-[10px] text-white/40 hover:text-white inline-flex items-center gap-1 bg-black/50 px-1.5 py-0.5 rounded"
                    ><Copy className="w-3 h-3" /> copy</button>
                </div>
                <div className="mt-3 text-[11px] text-white/50 leading-relaxed">
                    <code className="px-1 bg-white/5 rounded">filename</code> and{" "}
                    <code className="px-1 bg-white/5 rounded">site_id</code> are REQUIRED — missing
                    either returns <span className="font-mono text-amber-300">422</span> before the body is
                    read. Same payload returns the same job (SHA-256 dedup); set{" "}
                    <code className="px-1 bg-white/5 rounded">force_reextract=true</code> to bypass.
                </div>
            </div>
        </section>
    );
}


const RADAR_RECIPE_CURL = `# 1. Push ≤50 preprocessed opportunities (async → 202 + batch_id)
API="${INGEST_RECIPE_BASE}"
KEY="tf_live_..."            # tenant API key (or an admin JWT)

curl -X POST "$API/api/v1/ripeness/opportunities/batch" \\
  -H "Authorization: Bearer $KEY" -H "Content-Type: application/json" \\
  -H "Idempotency-Key: feed-2026-06-18-0001" \\
  -d '{"request_id":"feed-2026-06-18-0001","opportunities":[{"id":"6852b85fd480dc51ed964489",
       "compound_name":"Aficamten","sponsor":"Cytokinetics","therapeutic_area":"Cardiovascular",
       "indication":"Obstructive hypertrophic cardiomyopathy (HCM)","created_date":"2025-06-18T13:00:15.156Z",
       "details":"Following positive results from SEQUOIA-HCM pivotal Phase 3 ...",
       "article_url":"https://www.fiercebiotech.com/biotech/cytokinetics-aficamten-sequoia-hcm"}],
       "webhook_url":"https://example.com/hooks/ripeness"}'
# → {"batch_id":"opb_…","status":"queued","poll_url":"/api/v1/ripeness/opportunities/batch/opb_…", ...}
# article_url is required on every item (absolute http/https) — a missing one → 422 for the whole batch

# 2. Poll (or wait for the ripeness.batch.done webhook)
curl "$API/api/v1/ripeness/opportunities/batch/opb_…" -H "Authorization: Bearer $KEY"

# 3. Read the Radar — include / exclude lists, scalars, free text, sort, paging
#    study_role: core|clinpharm|label_expansion · timing: window_open_now|opening_within_12m|under_regulatory_review|overdue|frontier_running_long
#    cro_status: confirmed|likely|confirmed_or_likely|none — each with an exclude_* twin; rows carry program_track_count
curl "$API/api/v1/ripeness/tracks?tier=hot,warm&market_open=US,EU&study_role=core&timing=window_open_now,opening_within_12m\\
&exclude_cro_status=none&exclude_therapeutic_area=Oncology&ripeness_min=60&sort=last_signal_at:desc,ripeness:desc&limit=50" \\
  -H "Authorization: Bearer $KEY"

# 3b. Facet counts for the same selection (each facet counted without its own include — its exclude always applies)
curl "$API/api/v1/ripeness/facets?tier=hot,warm&market_open=US,EU&study_role=core&exclude_cro_status=none" -H "Authorization: Bearer $KEY"

# 4. Drill down + history
curl "$API/api/v1/ripeness/tracks/prg_baxdrostat:hypertension" -H "Authorization: Bearer $KEY"
curl "$API/api/v1/ripeness/tracks/prg_baxdrostat:hypertension/evaluations?limit=20" -H "Authorization: Bearer $KEY"

# 5. Persist an exclusion (tenant-scoped for keys, global for admins); DELETE to restore
curl -X POST "$API/api/v1/ripeness/exclusions" -H "Authorization: Bearer $KEY" -H "Content-Type: application/json" \\
  -d '{"scope":"track","target_id":"prg_olezarsen:dyslipidemia-lp-a","reason":"Sponsor already a client"}'

# 6. Saved view — store the /tracks/query body once, then apply it (list + facets from the same definition)
VIEW=$(curl -s -X POST "$API/api/v1/ripeness/views" -H "Authorization: Bearer $KEY" -H "Content-Type: application/json" \\
  -d '{"name":"US/EU core pipeline","body":{"include":{"tier":["hot","warm"],"market_open":["US","EU"],"study_role":["core"]},
       "exclude":{"cro_status":["none"]},"ripeness_min":60,"sort":["priority:desc"],"limit":50}}' | jq -r .view_id)
curl "$API/api/v1/ripeness/views/$VIEW/tracks?skip=0&limit=50" -H "Authorization: Bearer $KEY"
curl "$API/api/v1/ripeness/views/$VIEW/facets" -H "Authorization: Bearer $KEY"

# 7. Daily targeted re-evaluation (admin JWT) — preview the plan, start a run, poll it
curl "$API/api/v1/ripeness/reevaluations/plan?max_programs=50" -H "Authorization: Bearer $ADMIN_JWT"
curl -X POST "$API/api/v1/ripeness/reevaluations" -H "Authorization: Bearer $ADMIN_JWT" -H "Content-Type: application/json" \\
  -d '{"max_programs":50,"reason":"manual check after FDA calendar update"}'          # → 202 {"job_id":"rev_…"}
curl "$API/api/v1/ripeness/reevaluations/rev_…" -H "Authorization: Bearer $ADMIN_JWT"   # results.top_changes when done
`;

const RADAR_RECIPE_PYTHON = `"""Trial Ripeness Radar — push, poll, read with include/exclude + sort (httpx)."""
import httpx, time

API = "${INGEST_RECIPE_BASE}"
H = {"Authorization": "Bearer tf_live_..."}   # tenant API key (or admin JWT)

# 1. Push a batch (≤50 opportunities)
r = httpx.post(f"{API}/api/v1/ripeness/opportunities/batch", headers={**H, "Idempotency-Key": "feed-0001"}, json={
    "request_id": "feed-0001",
    "opportunities": [{"id": "6852b85fd480dc51ed964489", "compound_name": "Aficamten", "sponsor": "Cytokinetics",
                       "therapeutic_area": "Cardiovascular", "indication": "Obstructive hypertrophic cardiomyopathy (HCM)",
                       "created_date": "2025-06-18T13:00:15.156Z", "details": "Following positive results from SEQUOIA-HCM ...",
                       "article_url": "https://www.fiercebiotech.com/biotech/cytokinetics-aficamten-sequoia-hcm"}],  # required
}, timeout=30)
r.raise_for_status()
batch = r.json()                          # 202 → batch_id, items[].source_record_id, poll_url

# 2. Poll until terminal
while True:
    s = httpx.get(f"{API}{batch['poll_url']}", headers=H, timeout=30).json()
    if s["status"] in ("done", "failed", "cancelled"):
        break
    time.sleep(3)
for item in s["items"]:
    print(item["status"], [(t["track_id"], t["ripeness"], t["tier"]) for t in item.get("tracks", [])])

# 3. Read the Radar — JSON query form (long include / exclude lists)
view = {
    "include": {"tier": ["hot", "warm"], "market_open": ["US", "EU"], "study_role": ["core"],
                "timing": ["window_open_now", "opening_within_12m"]},
    "exclude": {"therapeutic_area": ["Oncology"], "track_id": ["prg_olezarsen:dyslipidemia-lp-a"], "cro_status": ["none"]},
    "ripeness_min": 60, "has_open_trials": True,
}
page = httpx.post(f"{API}/api/v1/ripeness/tracks/query", headers=H, json={
    **view, "sort": ["last_signal_at:desc", "ripeness:desc"], "skip": 0, "limit": 50,
}, timeout=30).json()
for t in page["items"]:
    print(t["track_id"], t["ripeness"], t["study_role"], t["timing_flags"], t["cro"], f"{t['program_track_count']} tracks in program")

# 3b. Facet counts for the SAME saved view (each facet is counted without its own include; its exclude always applies)
facets = httpx.post(f"{API}/api/v1/ripeness/facets/query", headers=H, json=view, timeout=30).json()
print(facets["total_tracks"], facets["study_roles"], facets["timing"], facets["cro_status"])

# 4. Drill down on one track (phases P1–P4, driving NCT, trials, articles, CRO)
detail = httpx.get(f"{API}/api/v1/ripeness/tracks/{page['items'][0]['track_id']}", headers=H, timeout=30).json()
print(detail["evaluation"]["phases"]["3"], len(detail["evidence"]["registry"]), "trials")

# 5. Saved view — persist the body once; list + facets then come from the same definition
saved = httpx.post(f"{API}/api/v1/ripeness/views", headers=H, json={
    "name": "US/EU core pipeline", "description": "Hot/warm core tracks with open US/EU sites and a CRO",
    "body": {**view, "sort": ["priority:desc", "ripeness:desc"], "limit": 50},
}, timeout=30).json()                                   # 201 → view_id (409 if the name exists in your scope)
rows = httpx.get(f"{API}/api/v1/ripeness/views/{saved['view_id']}/tracks", headers=H, params={"skip": 0, "limit": 50}, timeout=30).json()
counts = httpx.get(f"{API}/api/v1/ripeness/views/{saved['view_id']}/facets", headers=H, timeout=30).json()
assert rows["total"] == counts["total_tracks"]

# 6. Daily targeted re-evaluation (admin JWT only): preview → run → poll
A = {"Authorization": "Bearer <admin JWT>"}
plan = httpx.get(f"{API}/api/v1/ripeness/reevaluations/plan", headers=A, params={"max_programs": 50}, timeout=60).json()
print(plan["selected"], plan["by_reason"])              # e.g. {'tracks': 78, 'programs': 50} {'due': 326, 'overdue': 120, ...}
job = httpx.post(f"{API}/api/v1/ripeness/reevaluations", headers=A, json={"max_programs": 50, "reason": "manual check"}, timeout=60).json()
while job["status"] in ("queued", "processing"):
    time.sleep(10)
    job = httpx.get(f"{API}/api/v1/ripeness/reevaluations/{job['job_id']}", headers=A, timeout=30).json()
print(job["results"]["tracks_changed"], job["results"]["top_changes"][:3])
`;

const RADAR_RECIPE_TABS = [
    { id: "curl",   label: "curl",   snippet: RADAR_RECIPE_CURL },
    { id: "python", label: "Python", snippet: RADAR_RECIPE_PYTHON },
];

function RipenessRadarRecipe() {
    const [tab, setTab] = useState("curl");
    const active = RADAR_RECIPE_TABS.find((t) => t.id === tab) || RADAR_RECIPE_TABS[0];
    const copy = (text, label) => {
        try { navigator.clipboard.writeText(text); toast.success(`${label} snippet copied`); }
        catch { /* clipboard blocked */ }
    };
    return (
        <section id="ripeness-radar-recipe" data-testid="ripeness-radar-recipe-section" className="mb-10">
            <div className="mb-3 flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h3 className="font-display text-xl tracking-tight flex items-center gap-2">
                        <Radar className="w-5 h-5 text-emerald-300" /> Trial Ripeness Radar · push → poll → read
                    </h3>
                    <p className="text-sm text-white/50 mt-1 max-w-3xl">
                        Push preprocessed opportunities, wait for the batch, then pull the scored pipeline with
                        include / exclude lists, scalar ranges, free-text <code className="px-1 bg-white/5 rounded">q</code>,
                        multi-key <code className="px-1 bg-white/5 rounded">sort</code> and persisted exclusions.
                        Sections: <a href="#ripeness-push" className="text-emerald-200 underline hover:text-white">push</a>,{" "}
                        <a href="#ripeness-read" className="text-emerald-200 underline hover:text-white">read</a>,{" "}
                        <a href="#ripeness-exclusions" className="text-emerald-200 underline hover:text-white">exclusions</a>,{" "}
                        <a href="#ripeness-views" className="text-emerald-200 underline hover:text-white">saved views</a>,{" "}
                        <a href="#ripeness-reeval" className="text-emerald-200 underline hover:text-white">daily re-evaluation</a>,{" "}
                        <a href="#ripeness-jobs" className="text-emerald-200 underline hover:text-white">backfill jobs</a>.
                    </p>
                </div>
                <a
                    href="/downloads/ripeness-radar-api.md"
                    download="ripeness-radar-api.md"
                    data-testid="ripeness-radar-guide-download"
                    className="text-xs text-emerald-200 hover:text-white border border-emerald-400/30 hover:border-emerald-300/60 rounded-full px-3 py-1.5 whitespace-nowrap inline-flex items-center gap-1.5 transition-colors"
                >
                    <Download className="w-3.5 h-3.5" /> Integration guide (.md)
                </a>
            </div>
            <div className="rounded-xl border border-emerald-400/30 bg-emerald-400/[0.04] p-5">
                <div className="text-[11px] uppercase tracking-wider text-emerald-200/80 mb-3">
                    Batch push → webhook / poll → filtered read → drill-down → exclude → saved view → daily re-evaluation
                </div>
                <div className="flex items-center gap-1 mb-2 flex-wrap" data-testid="ripeness-radar-recipe-tabs">
                    {RADAR_RECIPE_TABS.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`ripeness-radar-recipe-tab-${t.id}`}
                            className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border ${tab === t.id
                                ? "border-emerald-400/50 bg-emerald-400/15 text-emerald-100"
                                : "border-white/10 text-white/55 hover:border-white/20"}`}
                        >{t.label}</button>
                    ))}
                </div>
                <div className="relative">
                    <pre
                        className="text-[11px] bg-black/40 border border-white/10 rounded px-3 py-3 overflow-auto font-mono leading-relaxed text-emerald-100 whitespace-pre max-h-[480px]"
                        data-testid={`ripeness-radar-recipe-snippet-${active.id}`}
                    >
                        {active.snippet}
                    </pre>
                    <button
                        onClick={() => copy(active.snippet, active.label)}
                        data-testid={`ripeness-radar-recipe-copy-${active.id}`}
                        className="absolute top-2 right-2 text-[10px] text-white/40 hover:text-white inline-flex items-center gap-1 bg-black/50 px-1.5 py-0.5 rounded"
                    ><Copy className="w-3 h-3" /> copy</button>
                </div>
                <div className="mt-3 text-[11px] text-white/50 leading-relaxed">
                    Filter grammar: list filters (<code className="px-1 bg-white/5 rounded">tier</code>,{" "}
                    <code className="px-1 bg-white/5 rounded">therapeutic_area</code>, <code className="px-1 bg-white/5 rounded">compound</code>, …)
                    accept comma-separated or repeated values — OR within a field, AND across fields — and every one has an{" "}
                    <code className="px-1 bg-white/5 rounded">exclude_*</code> twin. Unknown <code className="px-1 bg-white/5 rounded">sort</code> field →{" "}
                    <span className="font-mono text-amber-300">422</span>. Tenant keys are read-only on the Radar; backfill jobs are admin-only
                    (<span className="font-mono text-amber-300">403</span>).
                </div>
            </div>
        </section>
    );
}


function PartnerProvisioningRecipe() {
    const [tab, setTab] = useState("curl");
    const active = RECIPE_TABS.find((t) => t.id === tab) || RECIPE_TABS[0];
    const copy = (text, label) => {
        try { navigator.clipboard.writeText(text); toast.success(`${label} snippet copied`); }
        catch { /* clipboard blocked */ }
    };

    return (
        <section id="partner-provisioning" data-testid="partner-provisioning-section" className="mb-10">
            <div className="mb-3 flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h3 className="font-display text-xl tracking-tight flex items-center gap-2">
                        <Rocket className="w-5 h-5 text-violet-300" /> Partner Provisioning · one-shot recipe
                    </h3>
                    <p className="text-sm text-white/50 mt-1 max-w-3xl">
                        From zero to a fully-onboarded tenant making its first flow call in
                        under 30&nbsp;seconds. Requires a SiteCentric <code className="px-1 bg-white/5 rounded">superdomain</code> API key
                        — paste yours into <code className="px-1 bg-white/5 rounded">SD_KEY</code> and you&apos;re live.
                    </p>
                </div>
            </div>
            <div className="rounded-xl border border-violet-400/30 bg-violet-400/[0.04] p-5">
                <div className="text-[11px] uppercase tracking-wider text-violet-200/80 mb-3">
                    Provision → capture key → first call
                </div>
                <div className="flex items-center gap-1 mb-2 flex-wrap" data-testid="partner-provisioning-tabs">
                    {RECIPE_TABS.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`partner-provisioning-tab-${t.id}`}
                            className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border ${tab === t.id
                                ? "border-violet-400/50 bg-violet-400/15 text-violet-100"
                                : "border-white/10 text-white/55 hover:border-white/20"}`}
                        >{t.label}</button>
                    ))}
                </div>
                <div className="relative">
                    <pre
                        className="text-[11px] bg-black/40 border border-white/10 rounded px-3 py-3 overflow-auto font-mono leading-relaxed text-violet-100 whitespace-pre max-h-[460px]"
                        data-testid={`partner-provisioning-snippet-${active.id}`}
                    >
                        {active.snippet}
                    </pre>
                    <button
                        onClick={() => copy(active.snippet, active.label)}
                        data-testid={`partner-provisioning-copy-${active.id}`}
                        className="absolute top-2 right-2 text-[10px] text-white/40 hover:text-white inline-flex items-center gap-1 bg-black/50 px-1.5 py-0.5 rounded"
                    ><Copy className="w-3 h-3" /> copy</button>
                </div>
                <div className="mt-3 text-[11px] text-white/50 leading-relaxed">
                    The provisioning call is idempotent on <code className="px-1 bg-white/5 rounded">client_id</code>;
                    a re-run for an existing tenant returns <span className="font-mono text-amber-300">409</span> with
                    the public material only — secrets are never re-issued. To rotate, use{" "}
                    <a href="#provision-client" className="text-violet-200 underline hover:text-white"><code>/api-key/rotate</code></a>.
                </div>
            </div>
        </section>
    );
}



// =============================================================
// Authentication card — auto-rendered from components.securitySchemes
// so any new scheme added on the backend appears here without a
// frontend change. Keeps the "always stays up to date" guarantee.
// =============================================================
const SCHEME_META = {
    ApiKeyAuth:           { label: "Tenant API key",  icon: KeyRound,    tone: "border-cyan-400/40 text-cyan-300 bg-cyan-400/5" },
    BearerJWT:            { label: "Admin JWT",       icon: ShieldCheck, tone: "border-emerald-400/40 text-emerald-300 bg-emerald-400/5" },
    BearerJWT_Superdomain:{ label: "Superdomain JWT", icon: Shield,      tone: "border-violet-400/50 text-violet-200 bg-violet-400/8" },
    CronSecret:           { label: "Cron secret",     icon: Lock,        tone: "border-amber-400/40 text-amber-200 bg-amber-400/5" },
    SyncKey:              { label: "Radar Sync key",  icon: KeyRound,    tone: "border-sky-400/40 text-sky-200 bg-sky-400/5" },
    LinkToken:            { label: "Link token",      icon: KeyRound,    tone: "border-sky-400/40 text-sky-200 bg-sky-400/5" },
};

function AuthBadge({ scheme }) {
    const meta = SCHEME_META[scheme] || { label: scheme, icon: Lock, tone: "border-white/15 text-white/60 bg-white/5" };
    const Icon = meta.icon;
    return (
        <span title={`Requires ${meta.label}`}
            className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${meta.tone}`}>
            <Icon className="w-3 h-3" /> {meta.label}
        </span>
    );
}

function AuthenticationSection({ spec }) {
    const schemes = spec?.components?.securitySchemes || {};
    const entries = Object.entries(schemes);
    if (!entries.length) return null;
    const copy = (text) => {
        try { navigator.clipboard.writeText(text); toast.success("Copied"); } catch { /* noop */ }
    };
    return (
        <section id="authentication" data-testid="api-ref-auth-section" className="mb-10">
            <div className="mb-3">
                <h3 className="font-display text-xl tracking-tight">Authentication</h3>
                <p className="text-sm text-white/50 mt-1 max-w-3xl">
                    Three modes, picked per-route by the lock badge on the right of each row.
                    Every request must include exactly one of these in the
                    <code className="px-1.5 mx-1 bg-white/5 rounded text-[11px]">Authorization</code>
                    header.
                </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {entries.map(([name, scheme]) => {
                    const meta = SCHEME_META[name] || { label: name, icon: Lock, tone: "border-white/15 text-white/60 bg-white/5" };
                    const Icon = meta.icon;
                    return (
                        <div key={name} className={`rounded-xl border p-4 ${meta.tone}`} data-testid={`auth-card-${name}`}>
                            <div className="flex items-center gap-2 mb-2">
                                <Icon className="w-4 h-4" />
                                <div className="font-display text-base">{meta.label}</div>
                            </div>
                            <div className="text-[11px] uppercase tracking-wider text-white/40 mb-2">{name}</div>
                            <pre className="text-[11px] bg-black/40 rounded px-2 py-1.5 mb-2 overflow-auto">
{`Authorization: Bearer <${scheme.bearerFormat || "token"}>`}
                            </pre>
                            <button
                                onClick={() => copy(`Authorization: Bearer <${scheme.bearerFormat || "token"}>`)}
                                className="text-[10px] text-white/50 hover:text-white inline-flex items-center gap-1"
                                data-testid={`auth-copy-${name}`}
                            ><Copy className="w-3 h-3" /> Copy header</button>
                            {scheme.description && (
                                <div className="mt-3 text-xs text-white/65 leading-relaxed whitespace-pre-wrap" style={{ wordBreak: "break-word" }}>
                                    {scheme.description}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            <div className="mt-4 rounded-xl border border-amber-400/30 bg-amber-400/[0.05] p-4" data-testid="api-ref-cost-note">
                <div className="flex items-center gap-2 mb-1">
                    <div className="font-display text-base text-amber-100">Billing — every response carries its cost</div>
                </div>
                <p className="text-xs text-white/65 leading-relaxed max-w-4xl">
                    Every successful JSON response under <code className="px-1 bg-white/5 rounded">/api/v1/</code> ends with a{" "}
                    <code className="px-1 bg-white/5 rounded">cost</code> block —{" "}
                    <code className="px-1 bg-white/5 rounded">{'{"total_cost": 0.2184, "call_detail": "Study Build: Axsome SOL-ADHD-301", "call_timestamp": "2026-06-18 11:47"}'}</code>{" "}
                    — the USD total of the AI processing for the call (LLM spend + reuse charges for cached or re-used documents, as one total).
                    The same values are sent as <code className="px-1 bg-white/5 rounded">X-Cost-Total</code> /{" "}
                    <code className="px-1 bg-white/5 rounded">X-Cost-Detail</code> / <code className="px-1 bg-white/5 rounded">X-Cost-Timestamp</code> headers.
                    For asynchronous work (study builds, ingest jobs, async flow runs) each poll repeats the job's running total — bill once when the
                    job is terminal; the completion webhook carries the same block.
                </p>
            </div>
        </section>
    );
}

// =============================================================
// Resolved schema rendering moved to ./ApiReferenceSchema.jsx
// (works around an infinite-loop in the @emergentbase/visual-edits
// Babel plugin when a self-recursive component lives in the same
// file as its caller).
// =============================================================

/** Tiny markdown-lite renderer. Handles the patterns used inside
 *  FastAPI docstrings (`### Heading`, **bold**, `inline code`,
 *  triple-backtick fenced code blocks, numbered + bulleted lists).
 *  Returns an array of React elements safe to render inside JSX.
 *  Kept inline + dependency-free to avoid pulling in react-markdown
 *  for what amounts to ~5 patterns. */
function renderMarkdownLite(text) {
    if (!text || typeof text !== "string") return null;
    // First, pull out fenced code blocks so their content doesn't get
    // re-parsed by the inline rules below.
    const segments = [];
    const fence = /```(\w*)\n([\s\S]*?)```/g;
    let lastIdx = 0; let m;
    while ((m = fence.exec(text)) !== null) {
        if (m.index > lastIdx) segments.push({ kind: "md", body: text.slice(lastIdx, m.index) });
        segments.push({ kind: "code", lang: m[1] || "", body: m[2] });
        lastIdx = m.index + m[0].length;
    }
    if (lastIdx < text.length) segments.push({ kind: "md", body: text.slice(lastIdx) });

    const inline = (s) => {
        // Order matters: code → bold → emphasis. Use placeholders so
        // a `code with **bold**` inside doesn't double-format.
        const parts = [];
        const re = /(`[^`]+`)|(\*\*[^*]+\*\*)/g;
        let li = 0; let mi;
        while ((mi = re.exec(s)) !== null) {
            if (mi.index > li) parts.push(s.slice(li, mi.index));
            if (mi[1]) parts.push(<code key={`${mi.index}c`} className="px-1 bg-white/5 rounded text-[11px] text-violet-100">{mi[1].slice(1, -1)}</code>);
            else if (mi[2]) parts.push(<strong key={`${mi.index}b`} className="text-white">{mi[2].slice(2, -2)}</strong>);
            li = mi.index + mi[0].length;
        }
        if (li < s.length) parts.push(s.slice(li));
        return parts.length ? parts : [s];
    };

    const out = [];
    let key = 0;
    for (const seg of segments) {
        if (seg.kind === "code") {
            out.push(
                <pre key={`pre-${key++}`} className="bg-black/40 border border-white/10 rounded px-3 py-2 my-2 overflow-auto font-mono text-[11px] leading-relaxed text-violet-100 whitespace-pre">
                    {seg.body}
                </pre>,
            );
            continue;
        }
        const lines = seg.body.split("\n");
        let buf = []; let inList = null; let items = [];
        const flushParagraph = () => {
            if (!buf.length) return;
            const text = buf.join(" ").trim();
            if (text) out.push(<p key={`p-${key++}`} className="leading-relaxed">{inline(text)}</p>);
            buf = [];
        };
        const flushList = () => {
            if (!inList) return;
            const Tag = inList === "ol" ? "ol" : "ul";
            out.push(
                <Tag key={`l-${key++}`} className={inList === "ol" ? "list-decimal pl-5 space-y-0.5" : "list-disc pl-5 space-y-0.5"}>
                    {items.map((it, i) => <li key={i}>{inline(it)}</li>)}
                </Tag>,
            );
            inList = null; items = [];
        };
        for (const raw of lines) {
            const line = raw.replace(/\s+$/, "");
            const heading = line.match(/^(#{1,6})\s+(.*)$/);
            const ol = line.match(/^\s*(\d+)\.\s+(.*)$/);
            const ul = line.match(/^\s*[-*]\s+(.*)$/);
            if (heading) {
                flushParagraph(); flushList();
                const level = Math.min(heading[1].length, 4);
                const cls = level <= 1 ? "font-display text-base text-white/90 mt-3"
                          : level === 2 ? "font-display text-sm text-white/85 mt-3"
                          : "font-display text-[13px] text-white/80 mt-3 uppercase tracking-wider";
                out.push(<div key={`h-${key++}`} className={cls}>{inline(heading[2])}</div>);
                continue;
            }
            if (ol) {
                flushParagraph();
                if (inList !== "ol") { flushList(); inList = "ol"; }
                items.push(ol[2]);
                continue;
            }
            if (ul) {
                flushParagraph();
                if (inList !== "ul") { flushList(); inList = "ul"; }
                items.push(ul[1]);
                continue;
            }
            if (!line) { flushParagraph(); flushList(); continue; }
            // continuation of current paragraph OR list item
            if (inList && items.length) {
                items[items.length - 1] += " " + line.trim();
            } else {
                buf.push(line);
            }
        }
        flushParagraph(); flushList();
    }
    return out;
}

function MultipartFieldsPanel({ schema, spec }) {
    const props = schema?.properties || {};
    const required = new Set(schema?.required || []);
    const rows = Object.entries(props);
    if (rows.length === 0) {
        return (
            <div className="text-white/50 text-[11px] italic">
                Multipart body — no fields described.
            </div>
        );
    }
    return (
        <div className="rounded border border-white/10 bg-white/[0.02] divide-y divide-white/5">
            {rows.map(([name, prop]) => {
                const isFile = prop.type === "string" && prop.format === "binary";
                const isFileArray = prop.type === "array" && prop.items?.format === "binary";
                const typeLabel = isFile ? "file (binary)"
                    : isFileArray ? "file[] (binary)"
                    : describeType(prop);
                const example = (prop.examples && prop.examples[0]) ?? prop.example ?? null;
                return (
                    <div key={name} className="px-3 py-2 text-[11px] grid grid-cols-12 gap-2">
                        <div className="col-span-3 font-mono text-white/85">
                            {name}
                            {required.has(name) && <span className="text-rose-400"> *</span>}
                        </div>
                        <div className="col-span-2 text-white/55 font-mono">{typeLabel}</div>
                        <div className="col-span-7 text-white/65">
                            {prop.description || <span className="italic text-white/40">no description</span>}
                            {example !== null && example !== undefined && (
                                <div className="text-emerald-300/80 font-mono mt-0.5">
                                    e.g. {String(example)}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function OperationDetail({ op, spec }) {
    const content = op.op.requestBody?.content || {};
    const reqSchema = content["application/json"]?.schema;
    const multipartSchema = content["multipart/form-data"]?.schema;
    const streamSchema = content["application/octet-stream"];
    const responses = op.op.responses || {};
    const authScheme = (op.op.security?.[0] && Object.keys(op.op.security[0])[0]) || null;
    const description = useMemo(() => renderMarkdownLite(op.op.description), [op.op.description]);
    const sampleBody = useMemo(() => {
        if (!reqSchema) return null;
        return sampleValue(inlineSchema(reqSchema, spec), spec);
    }, [reqSchema, spec]);
    const copy = (text, label) => {
        try { navigator.clipboard.writeText(text); toast.success(`${label} copied`); } catch { /* noop */ }
    };

    return (
        <div className="px-4 pb-4 text-xs space-y-3">
            {description && (
                <div className="text-white/65 space-y-2" data-testid="op-description">{description}</div>
            )}
            {Array.isArray(op.op["x-change-history"]) && <ChangeHistory history={op.op["x-change-history"]} />}
            {authScheme && (
                <div className="flex items-center gap-2">
                    <span className="text-white/40 text-[10px] uppercase tracking-wider">Auth</span>
                    <AuthBadge scheme={authScheme} />
                </div>
            )}
            {op.op["x-flow-slug"] && (
                <div className="rounded-lg border border-violet-400/30 bg-violet-400/5 px-3 py-2">
                    <span className="text-violet-300 font-medium">Dynamic flow:</span> {op.op["x-flow-slug"]} · v{op.op["x-flow-version"]} · env: {op.op["x-flow-environment"]}
                </div>
            )}
            {op.op.parameters?.length > 0 && (
                <div>
                    <div className="text-white/40 uppercase tracking-wider text-[10px] mb-1">Parameters</div>
                    <ul className="space-y-1">
                        {op.op.parameters.map((p, i) => (
                            <li key={`${p.in}:${p.name}:${i}`} className="font-mono">
                                <span className="text-white/80">{p.name}</span><span className="text-white/40"> ({p.in})</span>
                                {p.required && <span className="text-rose-400"> *</span>}
                                {p.description && <span className="text-white/50"> — {p.description}</span>}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {reqSchema && (
                <div>
                    <div className="text-white/40 uppercase tracking-wider text-[10px] mb-1">Request body</div>
                    <ResolvedSchema schema={reqSchema} spec={spec} />
                </div>
            )}
            {multipartSchema && (
                <div>
                    <div className="text-white/40 uppercase tracking-wider text-[10px] mb-1">Form fields (multipart/form-data)</div>
                    <MultipartFieldsPanel schema={inlineSchema(multipartSchema, spec)} spec={spec} />
                </div>
            )}
            {streamSchema && (
                <div>
                    <div className="text-white/40 uppercase tracking-wider text-[10px] mb-1">Request body (binary stream)</div>
                    <div className="rounded border border-cyan-400/20 bg-cyan-400/5 px-3 py-2 text-cyan-200/90 text-[11px] leading-relaxed">
                        <div>Send the raw file bytes as the request body.</div>
                        <div className="font-mono mt-1 text-white/70">
                            Content-Type: application/octet-stream
                        </div>
                        <div className="text-white/55 mt-1">
                            Use the query parameters above to attach filename + metadata. The server enforces a 100&nbsp;MB hard cap and streams the upload to disk in 1&nbsp;MB chunks.
                        </div>
                    </div>
                </div>
            )}
            {sampleBody && (
                <div>
                    <div className="flex items-center justify-between mb-1">
                        <span className="text-white/40 uppercase tracking-wider text-[10px]">Example body</span>
                        <button onClick={() => copy(JSON.stringify(sampleBody, null, 2), "Example body")}
                            className="text-[10px] text-white/40 hover:text-white inline-flex items-center gap-1"
                            data-testid={`copy-body-${op.method}-${op.path}`}>
                            <Copy className="w-3 h-3" /> copy
                        </button>
                    </div>
                    <pre className="text-[10px] bg-black/40 border border-white/10 rounded px-3 py-2 overflow-auto max-h-72 font-mono leading-relaxed text-emerald-200">
                        {JSON.stringify(sampleBody, null, 2)}
                    </pre>
                </div>
            )}
            <SampleCodeTabs op={op} spec={spec} />
            <TryItPanel op={op} spec={spec} />
            {Object.keys(responses).length > 0 && (
                <div>
                    <div className="text-white/40 uppercase tracking-wider text-[10px] mb-1">Responses</div>
                    <div className="space-y-2">
                        {Object.entries(responses).map(([code, resp]) => {
                            const respSchema = resp?.content?.["application/json"]?.schema;
                            return (
                                <ResponseRow
                                    key={code}
                                    code={code}
                                    description={resp.description || ""}
                                    schema={respSchema}
                                    spec={spec}
                                    onCopy={copy}
                                    opKey={`${op.method}-${op.path}`}
                                />
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

/** Renders ONE response row — status code, description, the resolved
 *  schema property table (when present), AND a generated example body
 *  (when the schema has enough information to build one).
 *
 *  Two important UX bits:
 *   * Schemas with NO resolvable properties (i.e. a raw
 *     ``Dict[str, Any]`` on the server) get a placeholder block that
 *     tells the developer to declare a ``response_model=`` on the
 *     route — turning the missing-example into actionable feedback.
 *   * When the schema DOES resolve, the example mirrors exactly what
 *     a successful call returns, so customers can copy it straight
 *     into their integration tests.
 */
function ResponseRow({ code, description, schema, spec, onCopy, opKey }) {
    const sample = useMemo(() => {
        if (!schema) return null;
        const inlined = inlineSchema(schema, spec);
        return sampleValue(inlined, spec);
    }, [schema, spec]);
    const isRich = sample && typeof sample === "object" && Object.keys(sample).length > 0;
    const codeTone = code.startsWith("2") ? "bg-emerald-400/15 text-emerald-300"
        : code.startsWith("4") ? "bg-amber-400/15 text-amber-300"
        : code.startsWith("5") ? "bg-rose-400/15 text-rose-300"
        : "bg-white/10 text-white/60";
    return (
        <div className="rounded border border-white/10 px-3 py-2" data-testid={`response-${opKey}-${code}`}>
            <div className="flex items-center gap-2 mb-1">
                <code className={`text-[10px] px-1.5 py-0.5 rounded ${codeTone}`}>{code}</code>
                <span className="text-white/55">{description}</span>
            </div>
            {schema && <ResolvedSchema schema={schema} spec={spec} />}
            {sample !== null && (
                <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                        <span className="text-white/40 uppercase tracking-wider text-[10px]">Example response</span>
                        <button onClick={() => onCopy(JSON.stringify(sample, null, 2), "Example response")}
                            className="text-[10px] text-white/40 hover:text-white inline-flex items-center gap-1"
                            data-testid={`copy-response-${opKey}-${code}`}>
                            <Copy className="w-3 h-3" /> copy
                        </button>
                    </div>
                    <pre className={`text-[10px] bg-black/40 border border-white/10 rounded px-3 py-2 overflow-auto max-h-72 font-mono leading-relaxed ${code.startsWith("2") ? "text-emerald-200" : "text-amber-200"}`}>
                        {JSON.stringify(sample, null, 2)}
                    </pre>
                </div>
            )}
            {schema && !isRich && code.startsWith("2") && (
                // Server hasn't declared a `response_model=` on this route, so
                // FastAPI emits a bare `Object` schema. Tell the developer
                // exactly what to do — keeps the doc useful AND drives
                // server-side improvements.
                <div className="mt-2 text-[10px] text-white/40 italic" data-testid={`response-needs-model-${opKey}-${code}`}>
                    No <code>response_model=</code> on this route — the doc page can&apos;t
                    generate a fully-typed example. Add a Pydantic response model on the
                    handler to surface the field-level schema here automatically.
                </div>
            )}
        </div>
    );
}
